/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { 
  Calendar, Activity, ChevronRight, ChevronLeft,
  Dumbbell, ArrowLeft, ArrowRight,
  LogOut, Loader, MapPin, Lock, BookOpen,
  History, TrendingUp, Target, CheckCircle2, User,
  ClipboardList, FileText, Download, X, Film, PlayCircle, Mail, Instagram, Phone, Shield, Printer,
  Headphones, HelpCircle, Clock, Plus, Apple
} from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { getFirestore, collection, doc, setDoc, updateDoc, onSnapshot, getDoc, addDoc, deleteDoc } from 'firebase/firestore';

// --- FIREBASE CONFIGURATION ---
const firebaseConfig = {
  apiKey: "AIzaSyDFQBvgGXfNX5DEsW5tq8YV8_w3hBBxaOQ", 
  authDomain: "breathe-to-move-697c0.firebaseapp.com", 
  projectId: "breathe-to-move-697c0", 
  storageBucket: "breathe-to-move-697c0.firebasestorage.app", 
  messagingSenderId: "871044952745", 
  appId: "871044952745" 
};

// --- APP INITIALIZATION ---
let auth, db;
if (firebaseConfig.apiKey) {
  try {
    const app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
  } catch (e) { console.error("Firebase Init Error:", e); }
}

// --- COACH CREDENTIALS ---
const COACH_EMAIL = "breathetomovestudio@gmail.com";
const COACH_PASSWORD = "mh97admin"; 

// --- STYLES ---
const styles = `
  @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  .bg-wellness { background: linear-gradient(135deg, #f8fafc 0%, #eef6fb 100%); min-height: 100vh; }
  .glass-card { background: rgba(255, 255, 255, 0.95); backdrop-filter: blur(12px); border: 1px solid rgba(255, 255, 255, 0.7); box-shadow: 0 4px 20px -5px rgba(0,0,0,0.03); }
  .btn-action { transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
  .btn-action:active { transform: scale(0.97); }
  .animate-in { animation: fadeUp 0.4s ease-out forwards; }
  .scrollbar-hide::-webkit-scrollbar { display: none; }
  
  /* Triangle Marker Animation */
  @keyframes bounce-mark {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
  }
  .marker-anim { animation: bounce-mark 1.5s infinite ease-in-out; }

  /* Print Styles for the PDF Report */
  @media print {
    body * { visibility: hidden; }
    #printable-report, #printable-report * { visibility: visible; }
    #printable-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0; margin: 0; box-shadow: none; background: white; }
    .no-print { display: none !important; }
    .print-break-inside-avoid { break-inside: avoid; }
  }
`;

// --- HELPER: YOUTUBE ID EXTRACTOR ---
const getYoutubeId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

// --- CLINICAL DATA SCHEMAS ---
const CLINICAL_SCHEMAS_GRID = {
  bodyComp: { title: "Body Metrics Overview", unit: "", fields: { weight: "Weight", height: "Height", bmi: "BMI", hr: "Resting HR", bp: "Blood Pressure", hipWaist: "Hip to Waist Ratio" } },
  circumference: { title: "Circumference Measurements", unit: "cm", fields: { upperArm: "Upper Arm", chest: "Chest", waist: "Waist", hip: "Hip", quad: "Quadriceps", calves: "Calves" } },
  bodyFat: { title: "Body Fat % (Skinfolds)", unit: "cm", fields: { tricep: "Tricep", bicep: "Bicep", subscap: "Subscapularis", iliacus: "Iliacus" } },
  goniometry: { title: "Direct Flexibility (Goniometry)", unit: "degrees", fields: { shoulderFlex: "Shoulder Flexion", shoulderExt: "Shoulder Extension", elbowExt: "Elbow Extension", hipFlexBent: "Hip Flex (Bent Knee)", hipFlexStraight: "Hip Flex (Straight Leg)", hipExt: "Hip Extension", ankleDorsi: "Ankle Dorsiflexion", anklePlantar: "Ankle Plantarflexion" } },
  cardioEndurance: { title: "Cardiovascular Endurance (Aerobic)", unit: "", fields: { walkRunMeters: "12 Min Walk/Run (Meters)", vo2Max: "Estimated VO2 Max" } }
};

const CLINICAL_SCHEMAS_TEXT = {
  indirectFlex: { title: "Indirect Flexibility Testing", fields: { backScratch: "Back Scratch Test (Upper Body)", sitReach: "Sit and Reach Test (Lower Body)" } },
  functionalTest: { title: "Functional Testing", fields: { gait: "Dynamic Walking Gait Assessment", stepUp: "Single Leg Step Up Test" } },
  neuroTest: { title: "Neuromuscular Testing", fields: { stork: "Stork Stand Test" } },
  muscularEndurance: { title: "Muscular Endurance Testing", fields: { sitUp: "60 Sec Sit Up Test (Reps & Obs)", pushUp: "60 Sec Push Up Test (Reps & Obs)" } }
};

// --- COMPONENTS ---
const BodyMap = ({ markers = [], onMapClick, isEditing }) => (
  <div className="relative w-full max-w-[220px] aspect-[1/2.1] mx-auto bg-slate-50/50 rounded-[3rem] border border-slate-200 shadow-inner flex items-center justify-center overflow-hidden touch-none select-none">
    <svg viewBox="0 0 100 200" className="h-[85%] w-full text-slate-300 fill-current opacity-60 pointer-events-none">
      <circle cx="50" cy="20" r="12" />
      <path d="M40,35 Q20,40 15,60 L10,100 L20,105 L25,70 L35,80 L35,130 L25,180 L35,190 L45,140 L50,130 L55,140 L65,190 L75,180 L65,130 L65,80 L75,70 L80,105 L90,100 L85,60 Q80,40 60,35 Z" />
    </svg>
    <div 
      className={`absolute inset-0 z-10 ${isEditing ? 'cursor-crosshair' : 'pointer-events-none'}`} 
      onClick={(e) => {
        if (!isEditing) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        onMapClick(x, y);
      }}
    />
    {(Array.isArray(markers) ? markers : []).map((m, idx) => (
      <div 
        key={idx} 
        className="absolute w-0 h-0 z-20 pointer-events-none marker-anim" 
        style={{ 
          left: `calc(${m.x}% - 6px)`, 
          top: `calc(${m.y}% - 12px)`, 
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '10px solid #ef4444', 
          filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.2))'
        }}
      />
    ))}
  </div>
);

const ClinicalGrid = ({ schemaKey, schemaObj, isEdit, dataSource, onChange }) => (
  <div className="glass-card p-6 rounded-[2.5rem] shadow-sm border-white mb-6">
    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-800 border-b pb-3 mb-4">{schemaObj.title} {schemaObj.unit && <span className="text-slate-400 normal-case tracking-normal">({schemaObj.unit})</span>}</h3>
    <div className="grid grid-cols-2 gap-4">
      {Object.entries(schemaObj.fields).map(([k, label]) => (
        <div key={k}>
          <label className="text-[7px] font-black uppercase text-slate-400 ml-1">{label}</label>
          {isEdit ? (
            <input className="w-full p-3 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1" defaultValue={dataSource?.[schemaKey]?.[k] || ""} onBlur={e => onChange(`${schemaKey}.${k}`, e.target.value)} />
          ) : (
            <p className="text-sm font-bold text-slate-800 tracking-tight mt-1">{dataSource?.[schemaKey]?.[k] || "---"}</p>
          )}
        </div>
      ))}
    </div>
  </div>
);

const ClinicalTextAreas = ({ schemaKey, schemaObj, isEdit, dataSource, onChange }) => (
  <div className="glass-card p-6 rounded-[2.5rem] shadow-sm border-white mb-6">
    <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-800 border-b pb-3 mb-4">{schemaObj.title}</h3>
    <div className="space-y-4">
      {Object.entries(schemaObj.fields).map(([k, label]) => (
        <div key={k}>
          <label className="text-[7px] font-black uppercase text-slate-400 ml-1">{label}</label>
          {isEdit ? (
            <textarea className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 min-h-[80px]" placeholder="Observations, outcomes, data..." defaultValue={dataSource?.[schemaKey]?.[k] || ""} onBlur={e => onChange(`${schemaKey}.${k}`, e.target.value)} />
          ) : (
            <p className="text-xs font-bold text-slate-700 bg-slate-50 p-4 rounded-xl mt-1 whitespace-pre-wrap leading-relaxed shadow-inner">{dataSource?.[schemaKey]?.[k] || "No data logged."}</p>
          )}
        </div>
      ))}
    </div>
  </div>
);

// --- TRAINING CALENDAR WITH INCORPORATED APPOINTMENTS ---
const TrainingCalendar = ({ client, updateClientStats, isCoach }) => {
  const [baseDate, setBaseDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); 
  const [selectedDate, setSelectedDate] = useState(null);
  const [logType, setLogType] = useState("");
  const [activityDesc, setActivityDesc] = useState("");
  const [logDuration, setLogDuration] = useState(30);
  
  // Fetch global appointments from Firestore
  const [appointments, setAppointments] = useState([]);

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'appointments'), (snap) => {
      const appts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAppointments(appts);
    });
    return () => unsub();
  }, []);

  const logs = client.trainingLogs || {};
  const clientFullName = `${client.name || ""} ${client.surname || ""}`.trim();
  const clientAppointments = appointments.filter(a => a.clientName === clientFullName);

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getWeekDays = (date) => {
    const days = [];
    const current = new Date(date);
    const dayOfWeek = current.getDay();
    const diff = current.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday start
    current.setDate(diff);
    for (let i = 0; i < 7; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    let firstDay = new Date(year, month, 1).getDay();
    firstDay = firstDay === 0 ? 6 : firstDay - 1; // Monday start
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null); 
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  };

  const displayDays = viewMode === 'week' ? getWeekDays(baseDate) : getMonthDays(baseDate);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const handlePrev = () => {
    const newDate = new Date(baseDate);
    if (viewMode === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setMonth(newDate.getMonth() - 1);
    setBaseDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(baseDate);
    if (viewMode === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setMonth(newDate.getMonth() + 1);
    setBaseDate(newDate);
  };

  const openModal = (d) => {
    if (!d) return;
    const dateStr = formatDate(d);
    setSelectedDate(dateStr);
    setLogType("");
    setActivityDesc("");
    setLogDuration(30);
  };

  const saveLog = () => {
    if (!logType) return;
    const currentLogs = Array.isArray(logs[selectedDate]) 
      ? logs[selectedDate] 
      : (logs[selectedDate] ? [logs[selectedDate]] : []);
      
    let finalType = logType;
    if (logType === 'Physical social activity' && activityDesc.trim()) {
       finalType = `Physical social activity: ${activityDesc.trim()}`;
    }
      
    const updatedLogs = [...currentLogs.filter(l => l.type !== 'Did not train today'), { type: finalType, duration: Number(logDuration) }];
    
    updateClientStats(`trainingLogs.${selectedDate}`, updatedLogs);
    setLogType("");
    setActivityDesc("");
    setLogDuration(30);
  };

  const deleteSpecificLog = (indexToRemove) => {
    const currentLogs = Array.isArray(logs[selectedDate]) 
      ? logs[selectedDate] 
      : (logs[selectedDate] ? [logs[selectedDate]] : []);
      
    const updatedLogs = currentLogs.filter((_, idx) => idx !== indexToRemove);
    updateClientStats(`trainingLogs.${selectedDate}`, updatedLogs.length > 0 ? updatedLogs : null);
  };
  
  // Update Firestore global schedule document to mark as completed
  const markApptCompleted = async (apptId) => {
     if (!db) return;
     try {
       await updateDoc(doc(db, 'appointments', apptId), { status: 'Completed' });
     } catch (err) {
       console.error("Failed to mark appointment complete", err);
     }
  };

  const selectedDayLogs = selectedDate ? (Array.isArray(logs[selectedDate]) ? logs[selectedDate] : (logs[selectedDate] ? [logs[selectedDate]] : [])) : [];
  const selectedDayAppts = selectedDate ? clientAppointments.filter(a => a.date === selectedDate).sort((a,b) => a.time.localeCompare(b.time)) : [];

  const todayStr = formatDate(new Date());
  const upcomingAppts = clientAppointments
    .filter(a => a.date >= todayStr)
    .sort((a,b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time));

  return (
    <div className="glass-card p-5 rounded-[2.5rem] shadow-sm border-white mt-4">
       <div className="flex justify-between items-center mb-6">
          <div className="flex gap-2">
             <button onClick={() => setViewMode('week')} className={`px-3 py-2 text-[9px] font-black uppercase rounded-xl transition-colors btn-action ${viewMode==='week' ? 'bg-teal-50 text-teal-700 border border-teal-100' : 'text-slate-400 bg-slate-50'}`}>Week</button>
             <button onClick={() => setViewMode('month')} className={`px-3 py-2 text-[9px] font-black uppercase rounded-xl transition-colors btn-action ${viewMode==='month' ? 'bg-teal-50 text-teal-700 border border-teal-100' : 'text-slate-400 bg-slate-50'}`}>Month</button>
          </div>
          <div className="flex items-center gap-2">
             <button onClick={handlePrev} className="p-2 bg-slate-50 rounded-lg text-slate-400 hover:text-teal-600 btn-action"><ChevronLeft size={16}/></button>
             <span className="text-[10px] font-black uppercase text-slate-800 w-24 text-center tracking-widest">{monthNames[baseDate.getMonth()]} {baseDate.getFullYear()}</span>
             <button onClick={handleNext} className="p-2 bg-slate-50 rounded-lg text-slate-400 hover:text-teal-600 btn-action"><ChevronRight size={16}/></button>
          </div>
       </div>

       <div className="grid grid-cols-7 gap-2 mb-2 text-center">
          {dayNames.map(day => <div key={day} className="text-[8px] font-black uppercase tracking-widest text-slate-400">{day}</div>)}
       </div>
       
       <div className="grid grid-cols-7 gap-2">
          {displayDays.map((d, i) => {
             if (!d) return <div key={`empty-${i}`} className="aspect-square w-full"></div>;
             const dateStr = formatDate(d);
             const dayLogs = Array.isArray(logs[dateStr]) ? logs[dateStr] : (logs[dateStr] ? [logs[dateStr]] : []);
             const dayAppts = clientAppointments.filter(a => a.date === dateStr);
             const isToday = formatDate(new Date()) === dateStr;
             
             const totalDuration = dayLogs.reduce((sum, curr) => sum + (curr.duration || 0), 0);
             const isRest = dayLogs.some(l => l.type === 'Did not train today'); 
             
             return (
               <div key={dateStr} onClick={() => openModal(d)} className={`aspect-square w-full rounded-xl flex flex-col items-center justify-center p-1 border cursor-pointer transition-all btn-action ${isToday ? 'border-teal-500 bg-teal-50/50 shadow-sm' : 'border-slate-100 bg-white hover:border-teal-300 shadow-sm'}`}>
                  <span className={`text-[10px] font-bold ${isToday ? 'text-teal-700' : 'text-slate-700'}`}>{d.getDate()}</span>
                  {isRest && <span className="text-[6px] font-black text-slate-300 mt-1 uppercase tracking-widest">Rest</span>}
                  
                  {!isRest && (dayLogs.length > 0 || dayAppts.length > 0) && (
                    <div className="flex flex-col items-center mt-1">
                      <div className="flex gap-0.5 mb-0.5 flex-wrap justify-center px-1">
                        {/* Red/Green Appointment Dots */}
                        {dayAppts.map(appt => <span key={`appt-${appt.id}`} className={`w-1.5 h-1.5 rounded-full ${appt.status === 'Completed' ? 'bg-green-500' : 'bg-red-500'}`}></span>)}
                        {/* Teal Training Log Dots */}
                        {dayLogs.map((_, idx) => <span key={`log-${idx}`} className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>)}
                      </div>
                      {totalDuration > 0 && <span className="text-[7px] font-black text-teal-700">{totalDuration}m</span>}
                    </div>
                  )}
               </div>
             );
          })}
       </div>

       {/* UPCOMING APPOINTMENTS LIST */}
       <div className="mt-8 pt-6 border-t border-slate-100 text-left">
          <h4 className="text-[11px] font-black uppercase tracking-widest text-slate-800 text-center mb-4">Upcoming Scheduled Sessions</h4>
          <div className="space-y-3 max-h-[250px] overflow-y-auto pr-1 scrollbar-hide">
            {upcomingAppts.length > 0 ? upcomingAppts.map(appt => {
                const badgeColors = {
                    'Confirmed': 'bg-teal-50 text-teal-700',
                    'Pending Confirmation': 'bg-orange-50 text-orange-700',
                    'Rescheduled': 'bg-blue-50 text-blue-700',
                    'Cancelled': 'bg-red-50 text-red-700',
                    'Completed': 'bg-slate-100 text-slate-600'
                };
                return (
                  <div key={appt.id} className="bg-slate-50 p-4 rounded-[1.5rem] border border-slate-100 flex flex-col gap-2">
                     <div className="flex justify-between items-start">
                        <div>
                           <h5 className="font-black text-[11px] text-slate-800 uppercase tracking-tight">
                             {isCoach ? (appt.sessionName || "Session") : "Coaching Session"}
                           </h5>
                           <p className="text-[10px] font-bold text-slate-500 mt-1 flex items-center gap-1"><Clock size={10}/> {appt.date} @ {appt.time}</p>
                        </div>
                        <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-1 rounded-md ${badgeColors[appt.status] || 'bg-slate-200 text-slate-700'}`}>{appt.status}</span>
                     </div>
                  </div>
                )
            }) : (
              <p className="text-[9px] font-black uppercase text-slate-300 text-center py-4 tracking-widest">No Upcoming Sessions</p>
            )}
          </div>
       </div>

       {selectedDate && (
         <div className="fixed inset-0 z-[100] flex items-start pt-12 md:items-center justify-center p-6 pb-24 bg-slate-900/40 backdrop-blur-md animate-in">
           <div className="bg-white w-full max-w-sm rounded-[3rem] p-8 shadow-2xl relative overflow-hidden flex flex-col max-h-[85vh]">
              <button onClick={() => setSelectedDate(null)} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 z-10 btn-action"><X size={20}/></button>
              
              <div className="overflow-y-auto scrollbar-hide pb-4 -mx-2 px-2">
                <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center mb-4"><Calendar size={20}/></div>
                <h3 className="text-2xl font-black uppercase tracking-tighter text-slate-800 mb-1 leading-none">Log Training</h3>
                <p className="text-[10px] font-bold text-teal-600 uppercase tracking-widest mb-6">{selectedDate}</p>

                {/* Render Appointments inside modal */}
                {selectedDayAppts.length > 0 && (
                  <div className="mb-6 space-y-2">
                    <h4 className="text-[9px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-100 pb-2 mb-3">Scheduled Sessions</h4>
                    {selectedDayAppts.map((appt) => (
                      <div key={appt.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl shadow-inner border border-slate-100">
                         <div>
                            <p className="text-[10px] font-bold text-slate-700 uppercase tracking-widest">
                               {isCoach ? (appt.sessionName || "Session") : "Coaching Session"}
                            </p>
                            <p className="text-[9px] font-black text-slate-500 mt-0.5">{appt.time} - {appt.status}</p>
                         </div>
                         {appt.status !== 'Completed' && (
                            <button onClick={() => markApptCompleted(appt.id)} className="px-3 py-2 text-[8px] font-black uppercase tracking-widest text-green-600 bg-white rounded-lg shadow-sm border border-green-100 btn-action flex items-center gap-1 hover:bg-green-50">
                              <CheckCircle2 size={12}/> Mark Complete
                            </button>
                         )}
                         {appt.status === 'Completed' && (
                            <span className="p-2 text-green-500"><CheckCircle2 size={16}/></span>
                         )}
                      </div>
                    ))}
                  </div>
                )}

                {selectedDayLogs.length > 0 && (
                  <div className="mb-6 space-y-2">
                    <h4 className="text-[9px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-100 pb-2 mb-3">Saved Activities</h4>
                    {selectedDayLogs.map((lg, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl shadow-inner border border-slate-100">
                         <div>
                            <p className="text-[10px] font-bold text-slate-700 uppercase tracking-widest">{lg.type}</p>
                            {lg.type !== 'Did not train today' && <p className="text-[9px] font-black text-teal-600 mt-0.5">{lg.duration} mins</p>}
                         </div>
                         <button onClick={() => deleteSpecificLog(idx)} className="p-2 text-red-400 hover:text-red-600 bg-white rounded-lg shadow-sm border border-red-50 btn-action"><X size={14}/></button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-6 mb-2 pt-2 border-t border-slate-100">
                   <h4 className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Add New Activity</h4>
                   <div>
                      <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Training Type</label>
                      <select className="w-full p-4 bg-slate-50 rounded-xl text-sm font-bold outline-none mt-1 shadow-inner text-slate-700 focus:ring-2 focus:ring-teal-400" value={logType} onChange={e => setLogType(e.target.value)}>
                         <option value="">Select...</option>
                         <option value="Cardio">Cardio</option>
                         <option value="Posture correction">Posture correction</option>
                         <option value="Bootcamp">Bootcamp</option>
                         <option value="Massage">Massage</option>
                         <option value="Physical social activity">Physical social activity</option>
                         <option value="Day 1">Day 1</option>
                         <option value="Day 2">Day 2</option>
                         <option value="Day 3">Day 3</option>
                      </select>
                   </div>
                   
                   {logType === 'Physical social activity' && (
                     <div className="animate-in mt-3">
                        <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Activity Description</label>
                        <input type="text" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-teal-400" value={activityDesc} onChange={e => setActivityDesc(e.target.value)} placeholder="e.g., Hiking, Tennis..." />
                     </div>
                   )}

                   {logType && logType !== 'Did not train today' && (
                     <div className="animate-in mt-3">
                        <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Duration (Minutes)</label>
                        <div className="flex items-center gap-4 mt-2 bg-slate-50 p-4 rounded-xl shadow-inner">
                           <input type="range" min="10" max="90" step="5" value={logDuration} onChange={e => setLogDuration(e.target.value)} className="flex-1 accent-teal-600" />
                           <span className="text-sm font-black text-teal-700 w-12 text-right">{logDuration}m</span>
                        </div>
                     </div>
                   )}
                </div>
              </div>

              {logType && <button onClick={saveLog} className="w-full py-4 mt-4 bg-teal-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg btn-action shrink-0">Save Session</button>}
           </div>
         </div>
       )}
    </div>
  );
};

// --- UCS CASE STUDY GRID ---
const UCSCaseStudyProtocol = () => (
  <div className="bg-indigo-50/50 p-6 rounded-3xl border border-indigo-100 mb-6 shadow-inner text-slate-800">
    <h4 className="text-[11px] font-black uppercase tracking-widest text-indigo-700 mb-1 text-center">15-Minute UCS Protocol</h4>
    <p className="text-[9px] font-bold uppercase tracking-widest text-indigo-500 text-center border-b border-indigo-200 pb-3 mb-4">Complete Every Second Day</p>
    <div className="space-y-4 text-xs font-medium leading-relaxed">
      <div>
        <p className="font-bold text-indigo-900 mb-1">1. Tissue Prep & Inhibition (3–4 min)</p>
        <ul className="list-disc pl-4 space-y-1 text-slate-600">
          <li>Doorway Pec Stretch: 2 x 30s</li>
          <li>Foam Roller Thoracic Extension: 2 x 30s</li>
          <li>Levator Scapulae Stretch: 2 x 30s / side</li>
        </ul>
      </div>
      <div>
        <p className="font-bold text-indigo-900 mb-1">2. Isolate the DNFs (4–5 min)</p>
        <ul className="list-disc pl-4 space-y-1 text-slate-600">
          <li>Supine Chin Tucks (Gravity Eliminated): 1 x 10 reps (3s holds)</li>
          <li>Banded Cervical Retractions: 2 x 10 reps (2s Iso Hold)</li>
        </ul>
      </div>
      <div>
        <p className="font-bold text-indigo-900 mb-1">3. Isolate the Scapular Retractors (5–6 min)</p>
        <ul className="list-disc pl-4 space-y-1 text-slate-600">
          <li>Straight Arm Band Pull apart: 3 x 10 reps (2s Iso Hold, 3–4s Eccentric). (Underhand grip)</li>
        </ul>
      </div>
      <p className="text-[9px] text-indigo-500 font-bold italic mt-4 text-center bg-white p-3 rounded-xl shadow-sm">Unsure? Check the Video Library for instructions.</p>
    </div>
  </div>
);

const CaseStudyGrid = ({ client, updateClientStats, isCoach }) => {
  const [confirmDay, setConfirmDay] = useState(null);
  const csData = client.caseStudyData || {};
  const startDateStr = csData.startDate;
  const logs = csData.logs || {};

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  if (!startDateStr) {
    return <div className="text-center py-10 text-[10px] font-black uppercase text-slate-400 tracking-widest glass-card rounded-[2rem]">Awaiting Start Date</div>;
  }

  const startDate = new Date(startDateStr);
  const days = [];
  for (let i = 0; i < 28; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    days.push(d);
  }

  const handleDayClick = (dateStr) => {
    if (isCoach) {
      const current = logs[dateStr];
      updateClientStats(`caseStudyData.logs.${dateStr}`, !current);
      return;
    }

    if (logs[dateStr]) {
      const timeLogged = logs[dateStr] === true ? 'Previously Logged' : logs[dateStr];
      alert(`This session is locked. Completed on: ${timeLogged}`);
      return;
    }

    setConfirmDay(dateStr);
  };

  const markCompleted = () => {
    if (!confirmDay) return;
    const timestamp = new Date().toLocaleString(); 
    updateClientStats(`caseStudyData.logs.${confirmDay}`, timestamp);
    setConfirmDay(null);
  };

  return (
    <div className="glass-card p-6 rounded-[2.5rem] shadow-sm border-white relative overflow-hidden">
      <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-800 border-b pb-3 mb-4 text-center">28-Day Compliance (Every Second Day)</h3>
      <div className="grid grid-cols-7 gap-2">
        {days.map((d, i) => {
          const dateStr = formatDate(d);
          const logData = logs[dateStr];
          const isCompleted = !!logData; 
          const isToday = formatDate(new Date()) === dateStr;
          
          return (
            <div 
              key={dateStr} 
              onClick={() => handleDayClick(dateStr)} 
              title={isCompleted && logData !== true ? `Completed: ${logData}` : ''}
              className={`aspect-square w-full rounded-xl flex flex-col items-center justify-center p-1 border transition-all cursor-pointer btn-action hover:scale-105 ${isCompleted ? 'bg-indigo-600 border-indigo-700 text-white shadow-md' : isToday ? 'border-indigo-400 bg-indigo-50 text-indigo-800 shadow-sm' : 'border-slate-100 bg-white text-slate-400 shadow-sm'}`}
            >
               <span className="text-[8px] font-black opacity-60 mb-0.5">D{i+1}</span>
               {isCompleted ? <CheckCircle2 size={12} className="text-white"/> : <span className="text-[10px] font-bold">{d.getDate()}</span>}
            </div>
          );
        })}
      </div>

      {confirmDay && (
        <div className="absolute inset-0 z-50 flex items-center justify-center p-4 bg-white/80 backdrop-blur-sm animate-in rounded-[2.5rem]">
           <div className="bg-white p-6 rounded-3xl shadow-2xl border border-slate-100 text-center w-full max-w-[250px]">
              <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3">
                 <CheckCircle2 size={24}/>
              </div>
              <h4 className="text-[11px] font-black uppercase text-slate-800 mb-2">Protocol Complete?</h4>
              <p className="text-[9px] text-slate-500 mb-6 leading-relaxed">
                Did you complete the 15-minute UCS protocol for <b>{confirmDay}</b>? Once logged, this cannot be undone.
              </p>
              <div className="flex gap-2">
                 <button onClick={() => setConfirmDay(null)} className="flex-1 py-3 bg-slate-50 text-slate-500 hover:bg-slate-100 rounded-xl text-[9px] font-black uppercase btn-action">Cancel</button>
                 <button onClick={markCompleted} className="flex-1 py-3 bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl text-[9px] font-black uppercase shadow-md btn-action">Yes, Done</button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const AssessmentReport = ({ snapshot, client, onBack }) => {
  const handlePrint = () => window.print();

  return (
    <div className="animate-in pb-20">
      <div className="flex justify-between items-center mb-6 no-print">
        <button onClick={onBack} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-teal-600 bg-white px-4 py-2 rounded-xl shadow-sm border border-slate-100 btn-action"><ArrowLeft size={14}/> Back</button>
        <button onClick={handlePrint} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-white bg-teal-600 hover:bg-teal-700 px-4 py-2 rounded-xl shadow-sm btn-action"><Printer size={14}/> Save PDF</button>
      </div>

      <div id="printable-report" className="bg-white rounded-[2rem] p-8 md:p-12 shadow-xl border border-slate-100">
        <div className="border-b-2 border-slate-100 pb-6 mb-8 text-center print-break-inside-avoid">
          <div className="w-16 h-16 bg-slate-50 rounded-2xl mx-auto mb-4 flex items-center justify-center border border-slate-100">
             <span className="text-teal-700 font-black text-lg">BTM</span>
          </div>
          <h1 className="text-2xl font-black uppercase tracking-tighter text-slate-900 leading-none mb-2">Clinical Assessment Report</h1>
          <p className="text-[10px] font-black tracking-widest text-slate-400 uppercase">Client: <span className="text-teal-600">{client.name} {client.surname || ""}</span> &bull; Date: <span className="text-teal-600">{snapshot.date}</span></p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {Object.entries(CLINICAL_SCHEMAS_GRID).map(([key, schema]) => (
            <div key={key} className="border border-slate-200 p-5 rounded-2xl print-break-inside-avoid">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-800 border-b border-slate-100 pb-2 mb-3">{schema.title} {schema.unit && <span className="text-slate-400 normal-case tracking-normal">({schema.unit})</span>}</h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(schema.fields).map(([k, label]) => (
                  <div key={k}>
                    <p className="text-[7px] font-black uppercase text-slate-400">{label}</p>
                    <p className="text-xs font-bold text-slate-800">{snapshot[key]?.[k] || "---"}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-6 mb-8">
          {Object.entries(CLINICAL_SCHEMAS_TEXT).map(([key, schema]) => (
             <div key={key} className="border border-slate-200 p-5 rounded-2xl print-break-inside-avoid">
               <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-800 border-b border-slate-100 pb-2 mb-3">{schema.title}</h3>
               <div className="space-y-4">
                 {Object.entries(schema.fields).map(([k, label]) => (
                   <div key={k}>
                     <p className="text-[8px] font-black uppercase text-teal-700 mb-1">{label}</p>
                     <p className="text-xs font-medium text-slate-700 whitespace-pre-wrap">{snapshot[key]?.[k] || "No notes logged."}</p>
                   </div>
                 ))}
               </div>
             </div>
          ))}
        </div>

        <div className="border border-slate-200 p-6 rounded-2xl print-break-inside-avoid">
          <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-800 border-b border-slate-100 pb-2 mb-4 text-center">Postural Analysis</h3>
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="w-full md:w-1/2">
              <BodyMap markers={snapshot.assessments?.posture || snapshot.posturalMap} isEditing={false} />
            </div>
            <div className="w-full md:w-1/2 space-y-4">
              <div>
                <p className="text-[8px] font-black uppercase text-teal-700 mb-1">Outcome & Flagged Areas</p>
                <p className="text-xs font-medium text-slate-700 whitespace-pre-wrap bg-slate-50 p-4 rounded-xl">{snapshot.posturalNotes || "No notes logged."}</p>
              </div>
              {snapshot.posturalPdfUrl && (
                <div className="no-print">
                  <p className="text-[8px] font-black uppercase text-slate-400 mb-1">Attached Document</p>
                  <a href={snapshot.posturalPdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[10px] font-bold text-white bg-purple-600 px-4 py-2 rounded-lg hover:bg-purple-700"><FileText size={14}/> Open Postural PDF</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- COACH SCHEDULE COMPONENT ---
function CoachSchedule({ setView, clients }) {
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [modalData, setModalData] = useState(null);
  const [error, setError] = useState("");
  const [filterClient, setFilterClient] = useState("");
  
  const [calViewMode, setCalViewMode] = useState('week');
  const [calBaseDate, setCalBaseDate] = useState(new Date());

  useEffect(() => {
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'appointments'), (snap) => {
      const appts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAppointments(appts);
    });
    return () => unsub();
  }, []);

  const openNewModal = () => {
     setError("");
     setModalData({ title: '', sessionName: '', clientName: '', date: selectedDate, time: '09:00', status: 'Pending Confirmation', isRepeating: false, repeatCount: 1 });
  };

  const openEditModal = (appt) => {
     setError("");
     setModalData({ ...appt, originalDate: appt.date, originalTime: appt.time, moveReason: '' });
  };

  const handleSave = async () => {
     setError("");
     
     const computedSessionName = modalData.sessionName !== undefined ? modalData.sessionName : (modalData.title || "");
     const computedClientName = modalData.clientName || "";
     
     if (!computedSessionName && !computedClientName) return setError("Please select a client or enter a session name.");
     if (!modalData.date || !modalData.time) return setError("Please fill all required fields.");

     let finalTitle = computedSessionName;
     if (computedClientName) {
        finalTitle = computedSessionName ? `${computedClientName} - ${computedSessionName}` : computedClientName;
     }

     const isEditing = !!modalData.id;
     const isMoved = isEditing && (modalData.originalDate !== modalData.date || modalData.originalTime !== modalData.time);

     if (isMoved && !modalData.moveReason) return setError("Please select who moved this session.");

     const apptDataToSave = {
         title: finalTitle,
         sessionName: computedSessionName,
         clientName: computedClientName,
         time: modalData.time,
         status: modalData.status,
         moveHistory: modalData.moveHistory || []
     };

     if (isEditing) {
        if (isMoved) {
           apptDataToSave.moveHistory = [
             { 
               dateMoved: new Date().toLocaleDateString(), 
               reason: modalData.moveReason, 
               oldDate: modalData.originalDate, 
               oldTime: modalData.originalTime,
               newDate: modalData.date,
               newTime: modalData.time
             },
             ...apptDataToSave.moveHistory
           ];
           apptDataToSave.status = "Rescheduled";
        }
        apptDataToSave.date = modalData.date;
        await updateDoc(doc(db, 'appointments', modalData.id), apptDataToSave);
     } else {
        const weeks = modalData.isRepeating ? parseInt(modalData.repeatCount) || 1 : 1;
        const [y, m, d] = modalData.date.split('-').map(Number);
        
        for (let i = 0; i < weeks; i++) {
           const curr = new Date(y, m - 1, d + (i * 7));
           const dateStr = `${curr.getFullYear()}-${String(curr.getMonth()+1).padStart(2,'0')}-${String(curr.getDate()).padStart(2,'0')}`;
           
           await addDoc(collection(db, 'appointments'), {
               ...apptDataToSave,
               date: dateStr
           });
        }
     }
     setModalData(null);
  };

  const handleDelete = async () => {
     if (!modalData.id) return;
     await deleteDoc(doc(db, 'appointments', modalData.id));
     setModalData(null);
  };

  const adjustDate = (daysOffset) => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const newDate = new Date(y, m - 1, d + daysOffset);
    const dateStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`;
    setSelectedDate(dateStr);
    setCalBaseDate(newDate);
  };

  const filteredAppointments = filterClient 
    ? appointments.filter(a => a.title.toLowerCase().includes(filterClient.toLowerCase()))
    : appointments;

  const dayAppts = filteredAppointments.filter(a => a.date === selectedDate).sort((a,b) => a.time.localeCompare(b.time));

  // --- Coach Calendar Logic ---
  const formatCalDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getWeekDays = (date) => {
    const days = [];
    const current = new Date(date);
    const dayOfWeek = current.getDay();
    const diff = current.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); 
    current.setDate(diff);
    for (let i = 0; i < 7; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    let firstDay = new Date(year, month, 1).getDay();
    firstDay = firstDay === 0 ? 6 : firstDay - 1; 
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null); 
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  };

  const displayDays = calViewMode === 'week' ? getWeekDays(calBaseDate) : getMonthDays(calBaseDate);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const handleCalPrev = () => {
    const newDate = new Date(calBaseDate);
    if (calViewMode === 'week') newDate.setDate(newDate.getDate() - 7);
    else newDate.setMonth(newDate.getMonth() - 1);
    setCalBaseDate(newDate);
  };

  const handleCalNext = () => {
    const newDate = new Date(calBaseDate);
    if (calViewMode === 'week') newDate.setDate(newDate.getDate() + 7);
    else newDate.setMonth(newDate.getMonth() + 1);
    setCalBaseDate(newDate);
  };

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto pb-24 animate-in">
       <div className="flex justify-between items-center mb-8 text-center">
          <button onClick={() => setView('list')} className="p-3 bg-white rounded-xl shadow-sm text-slate-400 btn-action"><ArrowLeft size={20}/></button>
          <div className="flex-1 text-center"><h2 className="font-black uppercase text-sm tracking-widest">Coaches Schedule</h2></div>
          <div className="w-10"></div>
       </div>

       <div className="glass-card p-4 rounded-[2.5rem] shadow-sm border-white mb-6 flex items-center justify-between">
          <button onClick={() => adjustDate(-1)} className="p-3 bg-slate-50 rounded-xl text-slate-400 hover:text-indigo-600 btn-action"><ChevronLeft size={20}/></button>
          <div className="flex-1 text-center">
             <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1 block">
               {(() => {
                 const [y, m, d] = selectedDate.split('-').map(Number);
                 const dateObj = new Date(y, m - 1, d);
                 const dayStr = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dateObj.getDay()];
                 return dayStr;
               })()}
             </label>
             <input type="date" value={selectedDate} onChange={e => { 
                const val = e.target.value;
                if (!val) return;
                setSelectedDate(val); 
                const [y, m, d] = val.split('-').map(Number);
                setCalBaseDate(new Date(y, m - 1, d)); 
             }} className="bg-transparent text-lg font-black text-indigo-600 outline-none text-center w-full cursor-pointer" />
          </div>
          <button onClick={() => adjustDate(1)} className="p-3 bg-slate-50 rounded-xl text-slate-400 hover:text-indigo-600 btn-action"><ChevronRight size={20}/></button>
       </div>

       <div className="space-y-4 mb-8">
          {dayAppts.length > 0 ? dayAppts.map(appt => {
             const borderColors = {
                 'Confirmed': 'border-l-teal-500',
                 'Pending Confirmation': 'border-l-orange-400',
                 'Rescheduled': 'border-l-blue-500',
                 'Cancelled': 'border-l-red-500',
                 'Completed': 'border-l-slate-400'
             };
             const badgeColors = {
                 'Confirmed': 'bg-teal-50 text-teal-700',
                 'Pending Confirmation': 'bg-orange-50 text-orange-700',
                 'Rescheduled': 'bg-blue-50 text-blue-700',
                 'Cancelled': 'bg-red-50 text-red-700',
                 'Completed': 'bg-slate-100 text-slate-600'
             };
             
             return (
               <div key={appt.id} onClick={() => openEditModal(appt)} className={`glass-card p-5 rounded-[2rem] border-l-4 cursor-pointer btn-action flex flex-col gap-3 shadow-sm ${borderColors[appt.status] || 'border-l-slate-200'}`}>
                  <div className="flex justify-between items-start">
                     <div>
                        <h4 className="font-black text-slate-800 uppercase tracking-tight">{appt.title}</h4>
                        <p className="text-[10px] font-bold text-slate-500 flex items-center gap-1 mt-1"><Clock size={12}/> {appt.time}</p>
                     </div>
                     <span className={`text-[8px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${badgeColors[appt.status] || 'bg-slate-100 text-slate-600'}`}>{appt.status}</span>
                  </div>
                  {appt.moveHistory && appt.moveHistory.length > 0 && (
                     <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Rescheduled {appt.moveHistory[0].reason}</p>
                        <p className="text-[9px] text-slate-500 mt-0.5">Original: <span className="line-through">{appt.moveHistory[0].oldDate} @ {appt.moveHistory[0].oldTime}</span></p>
                        <p className="text-[9px] text-indigo-600 font-bold mt-0.5">New: {appt.moveHistory[0].newDate || appt.date} @ {appt.moveHistory[0].newTime || appt.time}</p>
                     </div>
                  )}
               </div>
             )
          }) : (
             <div className="text-center py-12 glass-card rounded-[2.5rem] border-white shadow-sm">
                <Calendar size={32} className="mx-auto text-slate-300 mb-3" />
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">No Sessions Booked</p>
             </div>
          )}
       </div>

       <button onClick={openNewModal} className="w-full py-4 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg btn-action flex items-center justify-center gap-2 mb-8">
          <Plus size={16}/> Add Session
       </button>
       
       {/* --- SCHEDULE OVERVIEW CALENDAR --- */}
       <div className="mt-8 border-t border-slate-100 pt-8">
          <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-800 text-center mb-4">Schedule Overview</h3>
          
          <div className="mb-4 px-2">
             <select value={filterClient} onChange={e => setFilterClient(e.target.value)} className="w-full p-3 bg-white rounded-xl text-xs font-bold outline-none shadow-sm text-slate-700 border border-slate-100 focus:ring-2 focus:ring-indigo-400">
                <option value="">All Clients</option>
                {clients.map(c => <option key={c.id} value={`${c.name} ${c.surname || ""}`.trim()}>{c.name} {c.surname || ""}</option>)}
             </select>
          </div>

          <div className="glass-card p-5 rounded-[2.5rem] shadow-sm border-white mt-4">
             <div className="flex justify-between items-center mb-6">
                <div className="flex gap-2">
                   <button onClick={() => setCalViewMode('week')} className={`px-3 py-2 text-[9px] font-black uppercase rounded-xl transition-colors btn-action ${calViewMode==='week' ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'text-slate-400 bg-slate-50'}`}>Week</button>
                   <button onClick={() => setCalViewMode('month')} className={`px-3 py-2 text-[9px] font-black uppercase rounded-xl transition-colors btn-action ${calViewMode==='month' ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'text-slate-400 bg-slate-50'}`}>Month</button>
                </div>
                <div className="flex items-center gap-2">
                   <button onClick={handleCalPrev} className="p-2 bg-slate-50 rounded-lg text-slate-400 hover:text-indigo-600 btn-action"><ChevronLeft size={16}/></button>
                   <span className="text-[10px] font-black uppercase text-slate-800 w-24 text-center tracking-widest">{monthNames[calBaseDate.getMonth()]} {calBaseDate.getFullYear()}</span>
                   <button onClick={handleCalNext} className="p-2 bg-slate-50 rounded-lg text-slate-400 hover:text-indigo-600 btn-action"><ChevronRight size={16}/></button>
                </div>
             </div>

             <div className="grid grid-cols-7 gap-2 mb-2 text-center">
                {dayNames.map(day => <div key={day} className="text-[8px] font-black uppercase tracking-widest text-slate-400">{day}</div>)}
             </div>
             
             <div className="grid grid-cols-7 gap-2">
                {displayDays.map((d, i) => {
                   if (!d) return <div key={`empty-${i}`} className="aspect-square w-full"></div>;
                   const dateStr = formatCalDate(d);
                   const isSelected = selectedDate === dateStr;
                   const dayEvents = filteredAppointments.filter(a => a.date === dateStr);
                   
                   return (
                     <div key={dateStr} onClick={() => { setSelectedDate(dateStr); setCalBaseDate(d); }} className={`aspect-square w-full rounded-xl flex flex-col items-center justify-center p-1 border cursor-pointer transition-all btn-action ${isSelected ? 'border-indigo-500 bg-indigo-50/50 shadow-sm' : 'border-slate-100 bg-white hover:border-indigo-300 shadow-sm'}`}>
                        <span className={`text-[10px] font-bold ${isSelected ? 'text-indigo-700' : 'text-slate-700'}`}>{d.getDate()}</span>
                        {dayEvents.length > 0 && (
                          <div className="flex gap-0.5 mt-1 flex-wrap justify-center px-1">
                            {dayEvents.map((_, idx) => <span key={idx} className="w-1 h-1 rounded-full bg-indigo-500"></span>)}
                          </div>
                        )}
                     </div>
                   );
                })}
             </div>
          </div>
       </div>

       {modalData && (
         <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-md animate-in">
           <div className="bg-white w-full max-w-sm rounded-[3rem] p-8 shadow-2xl relative flex flex-col">
              <button onClick={() => setModalData(null)} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 btn-action"><X size={20}/></button>
              <h3 className="text-2xl font-black uppercase tracking-tighter text-slate-800 mb-6 leading-none border-b border-slate-100 pb-4">{modalData.id ? "Edit Session" : "New Session"}</h3>
              
              <div className="space-y-4 mb-6 max-h-[60vh] overflow-y-auto scrollbar-hide px-1">
                 <div>
                    <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Select Client</label>
                    <select className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-indigo-400 text-slate-700" value={modalData.clientName || ""} onChange={e => setModalData({...modalData, clientName: e.target.value})}>
                       <option value="">-- No Client Selected --</option>
                       {clients.map(c => {
                          const fullName = `${c.name} ${c.surname || ""}`.trim();
                          return <option key={c.id} value={fullName}>{fullName}</option>
                       })}
                    </select>
                 </div>
                 <div>
                    <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Session Name (Optional)</label>
                    <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-indigo-400" value={modalData.sessionName !== undefined ? modalData.sessionName : (modalData.title || "")} onChange={e => setModalData({...modalData, sessionName: e.target.value})} placeholder="e.g., Postural Assessment" />
                 </div>
                 <div className="grid grid-cols-2 gap-3">
                    <div>
                       <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Date {modalData.id && '(Change to Reschedule)'}</label>
                       <input type="date" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-indigo-400" value={modalData.date} onChange={e => setModalData({...modalData, date: e.target.value})} />
                    </div>
                    <div>
                       <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Time {modalData.id && '(Change to Reschedule)'}</label>
                       <input type="time" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-indigo-400" value={modalData.time} onChange={e => setModalData({...modalData, time: e.target.value})} />
                    </div>
                 </div>
                 <div>
                    <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Status</label>
                    <select className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-indigo-400" value={modalData.status} onChange={e => setModalData({...modalData, status: e.target.value})}>
                       <option value="Pending Confirmation">Pending Confirmation</option>
                       <option value="Confirmed">Confirmed</option>
                       <option value="Rescheduled">Rescheduled</option>
                       <option value="Completed">Completed</option>
                       <option value="Cancelled">Cancelled</option>
                    </select>
                 </div>

                 {!modalData.id && (
                    <div className="pt-4 border-t border-slate-100 mt-2">
                       <label className="relative inline-flex items-center cursor-pointer mb-2">
                          <input type="checkbox" className="sr-only peer" checked={modalData.isRepeating} onChange={e => setModalData({...modalData, isRepeating: e.target.checked})} />
                          <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                          <span className="ml-3 text-[10px] font-black uppercase text-slate-700 tracking-widest">Repeat Weekly?</span>
                       </label>
                       {modalData.isRepeating && (
                          <div className="animate-in mt-2">
                             <label className="text-[7px] font-black uppercase text-slate-400 ml-1">How Many Weeks?</label>
                             <input type="number" min="1" max="12" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-indigo-400" value={modalData.repeatCount} onChange={e => setModalData({...modalData, repeatCount: e.target.value})} placeholder="e.g., 4" />
                          </div>
                       )}
                    </div>
                 )}

                 {modalData.id && (modalData.originalDate !== modalData.date || modalData.originalTime !== modalData.time) && (
                    <div className="p-4 bg-orange-50 rounded-2xl border border-orange-100 animate-in">
                       <label className="text-[7px] font-black uppercase text-orange-600 ml-1">Reason for Moving Session *</label>
                       <select className="w-full p-3 bg-white rounded-xl text-xs font-bold outline-none mt-1 shadow-sm focus:ring-2 focus:ring-orange-400 text-orange-800" value={modalData.moveReason} onChange={e => setModalData({...modalData, moveReason: e.target.value})}>
                          <option value="">Select Reason...</option>
                          <option value="By Coach">Moved by Coach</option>
                          <option value="By Client">Moved by Client</option>
                       </select>
                    </div>
                 )}
                 {error && <p className="text-red-500 text-[9px] font-black uppercase tracking-widest">{error}</p>}
              </div>

              <div className="flex gap-2">
                 {modalData.id && <button onClick={handleDelete} className="p-4 bg-red-50 text-red-600 rounded-xl font-black btn-action border border-red-100"><X size={16}/></button>}
                 <button onClick={handleSave} className="flex-1 py-4 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg btn-action">Save Session</button>
              </div>
           </div>
         </div>
       )}
    </div>
  );
}

// --- MAIN APP COMPONENT ---
export default function App() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [appMode, setAppMode] = useState('login'); 
  const [clients, setClients] = useState([]);
  const [currentClient, setCurrentClient] = useState(null);

  useEffect(() => {
    if (!auth) { setLoading(false); return; }
    const init = async () => { try { await signInAnonymously(auth); } catch(e) { setLoading(false); } };
    init();
    const unsubscribe = onAuthStateChanged(auth, u => { setUser(u); setLoading(false); });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user || !db) return;
    const clientsRef = collection(db, 'clients');
    const unsub = onSnapshot(clientsRef, (snap) => {
      const list = snap.docs.map(d => ({id: d.id, ...d.data()}));
      setClients(list);
      if (currentClient) {
        const updated = list.find(c => c.id === currentClient.id);
        if (updated) setCurrentClient(updated);
      }
    }, (err) => console.error("Firestore Error:", err));
    return () => unsub();
  }, [user, currentClient?.id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-wellness"><Loader className="animate-spin text-teal-600" size={24} /></div>;

  return (
    <div className="bg-wellness font-sans text-slate-900 selection:bg-teal-100 antialiased">
      <style>{styles}</style>
      <div className="relative z-10">
        {appMode === 'landing' && <LandingView setAppMode={setAppMode} />}
        {appMode === 'login' && <LoginView setAppMode={setAppMode} setClient={setCurrentClient} clients={clients} />}
        {appMode === 'coach' && <CoachView clients={clients} setAppMode={setAppMode} setClient={setCurrentClient} />}
        {appMode === 'client' && <ClientView client={currentClient} setAppMode={setAppMode} />}
      </div>
    </div>
  );
}

// --- LANDING PAGE VIEW ---
function LandingView({ setAppMode }) {
  return (
    <div className="min-h-screen">
      <nav className="fixed w-full z-50 glass-card border-b-0 rounded-b-3xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center border border-teal-100 overflow-hidden">
               <img src="btm logo.jpg" alt="Logo" className="w-full h-full object-contain bg-white" onError={(e) => {e.target.style.display='none'; e.target.nextSibling.style.display='block'}} />
               <span style={{display: 'none'}} className="text-teal-700 font-black text-xs">BTM</span>
            </div>
            <span className="font-black uppercase tracking-tighter text-slate-800">Breathe to Move</span>
          </div>
          <div className="hidden md:flex gap-8 text-xs font-bold uppercase tracking-widest text-slate-500">
            <a href="#about" className="hover:text-teal-600 transition-colors">About</a>
            <a href="#services" className="hover:text-teal-600 transition-colors">Services</a>
            <a href="#contact" className="hover:text-teal-600 transition-colors">Contact Us</a>
          </div>
          <button onClick={() => setAppMode('login')} className="bg-slate-900 text-white px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-teal-700 transition-colors shadow-lg btn-action">
            Client Portal
          </button>
        </div>
      </nav>

      <section className="pt-40 pb-20 px-6 max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12">
        <div className="flex-1 space-y-8 animate-in text-center md:text-left">
          <div className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-[10px] font-black uppercase tracking-widest border border-teal-100">
            Professional Conditioning
          </div>
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-slate-900 leading-[0.9]">
            Optimize <br/><span className="text-teal-600">Your Movement.</span>
          </h1>
          <p className="text-slate-500 text-lg md:text-xl font-medium leading-relaxed max-w-xl mx-auto md:mx-0">
            Evidence-based strength, conditioning, and physical prehabilitation tailored to your unique biomechanics.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
            <button onClick={() => setAppMode('login')} className="bg-teal-600 text-white px-8 py-4 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-teal-700 transition-all shadow-lg flex items-center justify-center gap-2 btn-action group">
              Start Your Journey <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform"/>
            </button>
          </div>
        </div>
        <div className="flex-1 w-full max-w-md glass-card p-4 rounded-[3rem] shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500 hidden md:block">
          <img 
            src="mac pic.jpg" 
            alt="Training" 
            className="w-full h-[500px] object-cover object-top rounded-[2.5rem]"
            onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=2070&auto=format&fit=crop"; }}
          />
        </div>
      </section>

      <section id="about" className="py-24 bg-slate-900 text-white px-6">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter">Meet The Coach</h2>
          <p className="text-slate-300 text-lg leading-relaxed">
            Led by <strong>Macaela Henning</strong>, a highly qualified <span className="text-teal-400 font-bold">Exercise Specialist</span> and <span className="text-teal-400 font-bold">Sports Conditioning Coach</span>. 
            Breathe to Move bridges the gap between clinical rehabilitation and peak physical performance. Whether you are a high-level athlete returning to sport, or part of a special population requiring careful, tailored exercise protocols, your programming is rooted in advanced exercise science and precise functional testing. We specialize in working to correct postural deviations, as well as providing safe, effective programming for pregnancy, the elderly, and youth.
          </p>
        </div>
      </section>

      <section id="services" className="py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-slate-900">SERVICES</h2>
          <p className="text-slate-500 mt-4 font-medium uppercase tracking-widest text-xs">Comprehensive Biomechanical Support</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="glass-card p-10 rounded-[2.5rem] shadow-sm">
            <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center mb-6"><Target size={28}/></div>
            <h3 className="text-xl font-black uppercase text-slate-800 mb-3">Athletic Conditioning</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Periodized programming designed to increase power, speed, and resilience for competitive athletes through dedicated strength blocks and sport-specific training.
            </p>
          </div>
          <div className="glass-card p-10 rounded-[2.5rem] border-teal-200 shadow-md">
            <div className="w-14 h-14 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center mb-6"><Activity size={28}/></div>
            <h3 className="text-xl font-black uppercase text-slate-800 mb-3">Postural Analysis</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              In-depth clinical assessments including body mapping to identify muscular imbalances, directing targeted corrective exercise plans.
            </p>
          </div>
          <div className="glass-card p-10 rounded-[2.5rem] shadow-sm">
            <div className="w-14 h-14 bg-orange-50 text-orange-600 rounded-2xl flex items-center justify-center mb-6"><Shield size={28}/></div>
            <h3 className="text-xl font-black uppercase text-slate-800 mb-3">Special Populations</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Safe, specialized, and highly monitored exercise protocols for individuals with specific medical, metabolic, or biomechanical requirements.
            </p>
          </div>
        </div>
      </section>

      <section id="contact" className="py-24 bg-white px-6">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-slate-900">Get In Touch</h2>
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 mt-8 flex-wrap">
            <a href="mailto:breathetomovestudio@gmail.com" className="glass-card px-8 py-6 rounded-3xl flex flex-col items-center gap-4 hover:-translate-y-1 transition-transform border-slate-100 shadow-md w-full sm:w-64">
              <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center"><Mail size={24}/></div>
              <span className="font-black text-slate-800 uppercase tracking-widest text-xs">Email</span>
              <span className="text-slate-500 font-medium text-xs break-all text-center">breathetomovestudio@gmail.com</span>
            </a>
            <a href="tel:0617793517" className="glass-card px-8 py-6 rounded-3xl flex flex-col items-center gap-4 hover:-translate-y-1 transition-transform border-slate-100 shadow-md w-full sm:w-64">
              <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center"><Phone size={24}/></div>
              <span className="font-black text-slate-800 uppercase tracking-widest text-xs">Phone 1</span>
              <span className="text-slate-500 font-medium text-sm">061 779 3517</span>
            </a>
            <a href="tel:0610588026" className="glass-card px-8 py-6 rounded-3xl flex flex-col items-center gap-4 hover:-translate-y-1 transition-transform border-slate-100 shadow-md w-full sm:w-64">
              <div className="w-14 h-14 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center"><Phone size={24}/></div>
              <span className="font-black text-slate-800 uppercase tracking-widest text-xs">Phone 2</span>
              <span className="text-slate-500 font-medium text-sm">061 058 8026</span>
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-slate-50 py-16 px-6 border-t border-slate-200">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
          <div>
            <h4 className="text-2xl font-black uppercase tracking-tighter text-slate-900 mb-2">Breathe to Move</h4>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Exercise Science & Conditioning</p>
          </div>
          <div className="flex gap-6">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-slate-400 hover:text-teal-600 shadow-sm transition-colors cursor-pointer"><Mail size={20}/></div>
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-slate-400 hover:text-teal-600 shadow-sm transition-colors cursor-pointer"><Instagram size={20}/></div>
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-slate-400 hover:text-teal-600 shadow-sm transition-colors cursor-pointer"><MapPin size={20}/></div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// --- LOGIN ---
function LoginView({ setAppMode, setClient, clients }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(""); 
  const [error, setError] = useState("");

  const handleLogin = (e) => {
    e.preventDefault();
    const cleanEmail = email.toLowerCase().trim();
    
    // 1. Check if the user is the Coach
    if (cleanEmail === COACH_EMAIL && password === COACH_PASSWORD) {
      setAppMode('coach');
      return;
    }

    // 2. If not the coach, check if the user is a Client
    const foundClient = clients.find(c => c.email === cleanEmail);
    if (foundClient) { 
      // Verify client password
      if (foundClient.password === password || (!foundClient.password && password === "")) {
        setClient(foundClient); 
        setAppMode('client'); 
      } else {
        setError("Incorrect Password.");
      }
    } else {
      // Neither coach nor client matched
      setError("Invalid credentials or profile not found.");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 relative">
      {/* Kept the back button in case you ever want to link back to the landing page */}
      <button onClick={() => setAppMode('landing')} className="absolute top-8 left-8 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-teal-600 flex items-center gap-2 btn-action">
         <ArrowLeft size={14}/> Back to Website
      </button>
      <div className="max-w-sm w-full glass-card rounded-[3rem] p-10 text-center animate-in shadow-2xl">
        <div className="w-24 h-24 bg-white rounded-[1.5rem] mx-auto mb-6 flex items-center justify-center shadow-lg border border-slate-50 relative overflow-hidden">
           <img src="btm logo.jpg" alt="Logo" className="w-full h-full object-contain relative z-10 bg-white p-3" onError={(e) => { e.target.style.display='none'; }} />
           <div className="absolute inset-0 flex items-center justify-center text-teal-600 font-black text-lg">BTM</div>
        </div>
        <h1 className="text-2xl font-black text-slate-800 tracking-tighter uppercase mb-1 leading-none">Breathe<br/>to Move</h1>
        <p className="text-slate-400 text-[8px] font-black uppercase tracking-[0.3em] mb-10 text-center">Wellness Portal</p>
        
        <form onSubmit={handleLogin} className="space-y-5 animate-in">
          <input 
            type="email" 
            autoFocus 
            value={email} 
            onChange={e=>setEmail(e.target.value)} 
            placeholder="Enter Your Email" 
            className="w-full p-4 bg-slate-50 rounded-xl text-center text-sm font-bold focus:ring-2 focus:ring-teal-400 outline-none shadow-inner" 
          />
          <input 
            type="password" 
            value={password} 
            onChange={e=>setPassword(e.target.value)} 
            placeholder="Enter Your Password" 
            className="w-full p-4 bg-slate-50 rounded-xl text-center text-sm font-bold focus:ring-2 focus:ring-teal-400 outline-none shadow-inner" 
          />
          
          {error && <p className="text-red-500 text-[9px] font-black uppercase tracking-widest leading-tight">{error}</p>}
          <button type="submit" className="w-full py-4 text-white bg-teal-600 rounded-xl font-bold uppercase tracking-widest text-[10px] btn-action shadow-lg">
            Sign In
          </button>
        </form>
      </div>
    </div>
  );
}

// --- COACH VIEW ---
function CoachView({ clients, setAppMode, setClient }) {
  const [view, setView] = useState('list'); 
  const [activeClient, setActiveClient] = useState(null);
  const [menuLevel, setMenuLevel] = useState(null); 
  const [subTab, setSubTab] = useState('active'); 
  const [globalVideos, setGlobalVideos] = useState([]);
  const [globalPodcasts, setGlobalPodcasts] = useState([]); 
  
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState(""); 
  
  const [videoTitle, setVideoTitle] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  
  const [podcastTitle, setPodcastTitle] = useState(""); 
  const [podcastUrl, setPodcastUrl] = useState(""); 

  const [progName, setProgName] = useState("");
  const [progStart, setProgStart] = useState("");
  const [progEnd, setProgEnd] = useState("");
  const [progDays, setProgDays] = useState("");
  const [progDetails, setProgDetails] = useState("");
  const [progPdfUrl, setProgPdfUrl] = useState("");
  
  const [testDate, setTestDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  useEffect(() => {
    const fetchLibraries = async () => {
       const vidRef = doc(db, 'library', 'videos');
       const vidSnap = await getDoc(vidRef);
       if (vidSnap.exists()) setGlobalVideos(vidSnap.data().list || []);
       
       const podRef = doc(db, 'library', 'podcasts');
       const podSnap = await getDoc(podRef);
       if (podSnap.exists()) setGlobalPodcasts(podSnap.data().list || []);
    };
    fetchLibraries();
  }, []);

  const createClient = async () => {
    if (!newName || !newEmail || !newPassword) return alert("Please fill in all fields including a password.");
    try {
      const emailKey = newEmail.toLowerCase().trim();
      await setDoc(doc(db, 'clients', emailKey), {
        name: newName, 
        email: emailKey,
        password: newPassword, 
        medications: "",
        bodyComp: {}, circumference: {}, bodyFat: {}, goniometry: {}, cardioEndurance: {},
        indirectFlex: {}, functionalTest: {}, neuroTest: {}, muscularEndurance: {},
        assignedVideos: [], assignedPodcasts: [], trainingLogs: {},
        testResultsHistory: [], pastPrograms: [], activeProgram: null,
        posturalNotes: "", posturalPdfUrl: "", assessments: { posture: [] },
        hasSeenTutorial: false,
        isCaseStudyParticipant: false, 
        caseStudyData: { startDate: "", startMetrics: { cva: "", ssa: "", tension: "" }, endMetrics: { cva: "", ssa: "", tension: "" }, logs: {} }
      });
      setNewName(""); setNewEmail(""); setNewPassword(""); 
      alert("Success! Profile Established.");
    } catch (e) {
      alert("Error: " + e.message);
    }
  };

  const addGlobalVideo = async () => {
    if (!videoTitle || !videoUrl) return;
    const newVideo = { id: Date.now(), title: videoTitle, url: videoUrl };
    const updatedList = [...globalVideos, newVideo];
    await setDoc(doc(db, 'library', 'videos'), { list: updatedList });
    setGlobalVideos(updatedList);
    setVideoTitle(""); setVideoUrl("");
    alert("Video Added.");
  };

  const addGlobalPodcast = async () => {
    if (!podcastTitle || !podcastUrl) return;
    const newPod = { id: Date.now(), title: podcastTitle, url: podcastUrl };
    const updatedList = [...globalPodcasts, newPod];
    await setDoc(doc(db, 'library', 'podcasts'), { list: updatedList });
    setGlobalPodcasts(updatedList);
    setPodcastTitle(""); setPodcastUrl("");
    alert("Podcast Added.");
  };

  const toggleClientVideo = async (video) => {
    if (!activeClient) return;
    let currentAssigned = activeClient.assignedVideos || [];
    const exists = currentAssigned.some(v => v.id === video.id);
    let updated = exists ? currentAssigned.filter(v => v.id !== video.id) : [...currentAssigned, video];
    await updateDoc(doc(db, 'clients', activeClient.id), { assignedVideos: updated });
  };

  const toggleClientPodcast = async (podcast) => {
    if (!activeClient) return;
    let currentAssigned = activeClient.assignedPodcasts || [];
    const exists = currentAssigned.some(p => p.id === podcast.id);
    let updated = exists ? currentAssigned.filter(p => p.id !== podcast.id) : [...currentAssigned, podcast];
    await updateDoc(doc(db, 'clients', activeClient.id), { assignedPodcasts: updated });
  };

  const updateClientStats = async (path, val) => {
    if (!activeClient) return;
    await updateDoc(doc(db, 'clients', activeClient.id), { [path]: val });
  };

  const assignProgram = async () => {
    if (!activeClient || !progName) return alert("Title required.");
    const plan = { name: progName, startDate: progStart, endDate: progEnd, daysPerWeek: progDays, details: progDetails, pdfUrl: progPdfUrl, dateAssigned: new Date().toLocaleDateString(), id: Date.now() };
    await updateDoc(doc(db, 'clients', activeClient.id), { activeProgram: plan });
    setProgName(""); setProgStart(""); setProgEnd(""); setProgDays(""); setProgDetails(""); setProgPdfUrl("");
    alert("Program Assigned.");
  };

  const completePlan = async () => {
    if (!activeClient.activeProgram) return;
    const updatedHistory = [{ ...activeClient.activeProgram, dateCompleted: new Date().toLocaleDateString() }, ...(Array.isArray(activeClient.pastPrograms) ? activeClient.pastPrograms : [])];
    await updateDoc(doc(db, 'clients', activeClient.id), { pastPrograms: updatedHistory, activeProgram: null });
    alert("Plan Archived.");
  };

  const handleMarkerClick = async (x, y) => {
    if (!activeClient) return;
    const updated = [...(Array.isArray(activeClient.assessments?.posture) ? activeClient.assessments.posture : []), { x, y }];
    await updateDoc(doc(db, 'clients', activeClient.id), { 'assessments.posture': updated });
  };

  const saveClinicalSnapshot = async () => {
    if (!activeClient) return;
    const snapshot = { 
      date: testDate, 
      bodyComp: activeClient.bodyComp, 
      circumference: activeClient.circumference, 
      bodyFat: activeClient.bodyFat,
      goniometry: activeClient.goniometry,
      indirectFlex: activeClient.indirectFlex,
      functionalTest: activeClient.functionalTest,
      neuroTest: activeClient.neuroTest,
      muscularEndurance: activeClient.muscularEndurance,
      cardioEndurance: activeClient.cardioEndurance,
      posturalNotes: activeClient.posturalNotes, 
      posturalPdfUrl: activeClient.posturalPdfUrl,
      posturalMap: activeClient.assessments?.posture, 
      id: Date.now() 
    };
    const updatedHistory = [snapshot, ...(Array.isArray(activeClient.testResultsHistory) ? activeClient.testResultsHistory : [])];
    await updateDoc(doc(db, 'clients', activeClient.id), { testResultsHistory: updatedHistory });
    alert("Assessment Saved.");
  };

  const migrateOldSessions = async () => {
    try {
      const oldDocRef = doc(db, 'coach_settings', 'schedule');
      const oldSnap = await getDoc(oldDocRef);
      
      if (oldSnap.exists() && oldSnap.data().events) {
        const oldEvents = oldSnap.data().events;
        
        for (const event of oldEvents) {
          await addDoc(collection(db, 'appointments'), {
            title: event.title || "",
            sessionName: event.sessionName || "",
            clientName: event.clientName || "",
            date: event.date || "",
            time: event.time || "",
            status: event.status || "Confirmed",
            moveHistory: event.moveHistory || []
          });
        }
        alert(`Successfully migrated ${oldEvents.length} sessions! They should now appear on your calendar. You can now delete this button.`);
      } else {
        alert("No old sessions found in the database.");
      }
    } catch (err) {
      console.error("Migration failed:", err);
      alert("Migration failed. Check console for details.");
    }
  };

  if (view === 'schedule') {
     return <CoachSchedule setView={setView} clients={clients} />;
  }

  if (view === 'library') {
     return (
       <div className="min-h-screen p-6 max-w-lg mx-auto pb-24 animate-in">
         <div className="flex justify-between items-center mb-8 text-center">
            <button onClick={() => setView('list')} className="p-3 bg-white rounded-xl shadow-sm text-slate-400 btn-action"><ArrowLeft size={20}/></button>
            <div className="flex-1 text-center"><h2 className="font-black uppercase text-sm tracking-widest">Video Library</h2></div>
            <div className="w-10"></div>
         </div>
         <div className="glass-card rounded-[2.5rem] p-8 space-y-4">
            <input placeholder="Video Title" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={videoTitle} onChange={e=>setVideoTitle(e.target.value)} />
            <input placeholder="YouTube Link" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={videoUrl} onChange={e=>setVideoUrl(e.target.value)} />
            <button onClick={addGlobalVideo} className="w-full py-4 bg-orange-600 text-white rounded-xl text-[9px] font-black uppercase">Add to Library</button>
         </div>
         <div className="space-y-3 mt-6">
            {globalVideos.map(v => (
              <div key={v.id} className="glass-card p-4 rounded-xl flex justify-between items-center">
                <span className="font-bold text-xs uppercase text-slate-700">{v.title}</span>
              </div>
            ))}
         </div>
       </div>
     );
  }

  if (view === 'podcasts') {
    return (
      <div className="min-h-screen p-6 max-w-lg mx-auto pb-24 animate-in">
        <div className="flex justify-between items-center mb-8 text-center">
           <button onClick={() => setView('list')} className="p-3 bg-white rounded-xl shadow-sm text-slate-400 btn-action"><ArrowLeft size={20}/></button>
           <div className="flex-1 text-center"><h2 className="font-black uppercase text-sm tracking-widest">Podcast Library</h2></div>
           <div className="w-10"></div>
        </div>
        <div className="glass-card rounded-[2.5rem] p-8 space-y-4 border-teal-100">
           <input placeholder="Podcast Title (e.g., Huberman Lab)" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={podcastTitle} onChange={e=>setPodcastTitle(e.target.value)} />
           <input placeholder="Link (Spotify, YouTube, Apple)" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={podcastUrl} onChange={e=>setPodcastUrl(e.target.value)} />
           <button onClick={addGlobalPodcast} className="w-full py-4 bg-teal-600 text-white rounded-xl text-[9px] font-black uppercase shadow-md hover:bg-teal-700">Add to Library</button>
        </div>
        <div className="space-y-3 mt-6">
           {globalPodcasts.map(p => (
             <div key={p.id} className="glass-card p-4 rounded-xl flex justify-between items-center border-white">
               <div className="flex items-center gap-3">
                 <Headphones size={16} className="text-teal-600"/>
                 <span className="font-bold text-xs uppercase text-slate-700">{p.title}</span>
               </div>
             </div>
           ))}
        </div>
      </div>
    );
 }

  if (view === 'edit' && activeClient) {
    return (
      <div className="min-h-screen p-6 max-w-lg mx-auto pb-24 animate-in">
        <div className="flex justify-between items-center mb-8 text-center no-print">
           <button onClick={() => { if (menuLevel) setMenuLevel(null); else setView('list'); }} className="p-3 bg-white rounded-xl shadow-sm text-slate-400 btn-action"><ArrowLeft size={20}/></button>
           <div className="flex-1 text-center"><h2 className="font-black uppercase text-sm tracking-widest">{activeClient.name} {activeClient.surname || ""}</h2></div>
           <div className="w-10"></div>
        </div>
        {!menuLevel && (
          <div className="grid grid-cols-1 gap-4">
             <button onClick={() => setMenuLevel('casestudy')} className="glass-card p-8 rounded-[2.5rem] flex items-center justify-between group shadow-md btn-action border border-indigo-100 bg-indigo-50/30">
                <div className="flex items-center gap-5"><div className="p-4 bg-indigo-100 text-indigo-600 rounded-2xl"><BookOpen size={28}/></div><span className="font-black uppercase text-sm tracking-widest text-slate-800">UCS Case Study</span></div>
                <ChevronRight className="text-slate-300"/>
             </button>
             <button onClick={() => setMenuLevel('profile')} className="glass-card p-8 rounded-[2.5rem] flex items-center justify-between group shadow-md btn-action">
                <div className="flex items-center gap-5"><div className="p-4 bg-blue-50 text-blue-600 rounded-2xl"><User size={28}/></div><span className="font-black uppercase text-sm tracking-widest text-slate-800">Client Profile</span></div>
                <ChevronRight className="text-slate-300"/>
             </button>
             <button onClick={() => { setMenuLevel('training'); setSubTab('active'); }} className="glass-card p-8 rounded-[2.5rem] flex items-center justify-between group shadow-md btn-action">
                <div className="flex items-center gap-5"><div className="p-4 bg-teal-50 text-teal-600 rounded-2xl"><Dumbbell size={28}/></div><span className="font-black uppercase text-sm tracking-widest text-slate-800">Training Plans</span></div>
                <ChevronRight className="text-slate-300"/>
             </button>
             <button onClick={() => { setMenuLevel('tests'); setSubTab('clinical'); setSelectedSnapshot(null); }} className="glass-card p-8 rounded-[2.5rem] flex items-center justify-between group shadow-md btn-action">
                <div className="flex items-center gap-5"><div className="p-4 bg-purple-50 text-purple-600 rounded-2xl"><ClipboardList size={28}/></div><span className="font-black uppercase text-sm tracking-widest text-slate-800">Test Results</span></div>
                <ChevronRight className="text-slate-300"/>
             </button>
             <button onClick={() => setMenuLevel('videos')} className="glass-card p-8 rounded-[2.5rem] flex items-center justify-between group shadow-md btn-action">
                <div className="flex items-center gap-5"><div className="p-4 bg-orange-50 text-orange-600 rounded-2xl"><Film size={28}/></div><span className="font-black uppercase text-sm tracking-widest text-slate-800">Assign Videos</span></div>
                <ChevronRight className="text-slate-300"/>
             </button>
             <button onClick={() => setMenuLevel('podcasts')} className="glass-card p-8 rounded-[2.5rem] flex items-center justify-between group shadow-md btn-action">
                <div className="flex items-center gap-5"><div className="p-4 bg-blue-50 text-blue-600 rounded-2xl"><Headphones size={28}/></div><span className="font-black uppercase text-sm tracking-widest text-slate-800">Assign Podcasts</span></div>
                <ChevronRight className="text-slate-300"/>
             </button>
             <button onClick={() => setMenuLevel('account')} className="glass-card p-8 rounded-[2.5rem] flex items-center justify-between group shadow-md btn-action">
                <div className="flex items-center gap-5"><div className="p-4 bg-slate-100 text-slate-600 rounded-2xl"><Lock size={28}/></div><span className="font-black uppercase text-sm tracking-widest text-slate-800">Account Security</span></div>
                <ChevronRight className="text-slate-300"/>
             </button>
          </div>
        )}

        {/* --- COACH CASE STUDY VIEW --- */}
        {menuLevel === 'casestudy' && (
          <div className="space-y-6">
            <div className="glass-card p-8 rounded-[3rem] shadow-xl border-white space-y-6">
              <div className="flex items-center justify-between border-b pb-4 border-slate-100">
                <h3 className="text-[10px] font-black uppercase text-slate-400">Enroll in Study</h3>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" checked={!!activeClient.isCaseStudyParticipant} onChange={e => updateClientStats('isCaseStudyParticipant', e.target.checked)} />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
              
              {activeClient.isCaseStudyParticipant && (
                <div className="space-y-8 animate-in">
                  <div>
                    <label className="text-[7px] font-black uppercase text-indigo-400 ml-1">Study Start Date</label>
                    <input type="date" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-indigo-400" defaultValue={activeClient.caseStudyData?.startDate || ""} onBlur={e => updateClientStats('caseStudyData.startDate', e.target.value)} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
                      <h4 className="text-[9px] font-black uppercase text-slate-800 tracking-widest text-center border-b pb-2">Start Metrics</h4>
                      <div><label className="text-[7px] font-black uppercase text-slate-400">CVA Angle</label><input className="w-full p-2 bg-white rounded-lg text-xs font-bold outline-none mt-1 shadow-sm" defaultValue={activeClient.caseStudyData?.startMetrics?.cva || ""} onBlur={e => updateClientStats('caseStudyData.startMetrics.cva', e.target.value)} /></div>
                      <div><label className="text-[7px] font-black uppercase text-slate-400">SSA Angle</label><input className="w-full p-2 bg-white rounded-lg text-xs font-bold outline-none mt-1 shadow-sm" defaultValue={activeClient.caseStudyData?.startMetrics?.ssa || ""} onBlur={e => updateClientStats('caseStudyData.startMetrics.ssa', e.target.value)} /></div>
                      <div><label className="text-[7px] font-black uppercase text-slate-400">Subjective Tension (1-10)</label><input className="w-full p-2 bg-white rounded-lg text-xs font-bold outline-none mt-1 shadow-sm" defaultValue={activeClient.caseStudyData?.startMetrics?.tension || ""} onBlur={e => updateClientStats('caseStudyData.startMetrics.tension', e.target.value)} /></div>
                    </div>
                    
                    <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-100 space-y-3">
                      <h4 className="text-[9px] font-black uppercase text-indigo-900 tracking-widest text-center border-b border-indigo-200 pb-2">Day 28 Metrics</h4>
                      <div><label className="text-[7px] font-black uppercase text-indigo-400">CVA Angle</label><input className="w-full p-2 bg-white rounded-lg text-xs font-bold outline-none mt-1 shadow-sm" defaultValue={activeClient.caseStudyData?.endMetrics?.cva || ""} onBlur={e => updateClientStats('caseStudyData.endMetrics.cva', e.target.value)} /></div>
                      <div><label className="text-[7px] font-black uppercase text-indigo-400">SSA Angle</label><input className="w-full p-2 bg-white rounded-lg text-xs font-bold outline-none mt-1 shadow-sm" defaultValue={activeClient.caseStudyData?.endMetrics?.ssa || ""} onBlur={e => updateClientStats('caseStudyData.endMetrics.ssa', e.target.value)} /></div>
                      <div><label className="text-[7px] font-black uppercase text-indigo-400">Subjective Tension (1-10)</label><input className="w-full p-2 bg-white rounded-lg text-xs font-bold outline-none mt-1 shadow-sm" defaultValue={activeClient.caseStudyData?.endMetrics?.tension || ""} onBlur={e => updateClientStats('caseStudyData.endMetrics.tension', e.target.value)} /></div>
                    </div>
                  </div>

                  <CaseStudyGrid client={activeClient} updateClientStats={updateClientStats} isCoach={true} />
                </div>
              )}
            </div>
          </div>
        )}

        {menuLevel === 'profile' && (
          <div className="space-y-6">
             <div className="glass-card p-8 rounded-[3rem] shadow-xl border-white space-y-4 relative">
              <div className="flex justify-center mb-6">
                 <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-[2rem] flex items-center justify-center shadow-inner border border-blue-100"><User size={36}/></div>
              </div>
              
              <div className="space-y-3">
                 <div>
                    <label className="text-[7px] font-black uppercase text-slate-400 ml-1">First Name</label>
                    <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-blue-400" defaultValue={activeClient.name || ""} onBlur={e => updateClientStats('name', e.target.value)} />
                 </div>
                 <div>
                    <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Surname</label>
                    <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-blue-400" defaultValue={activeClient.surname || ""} onBlur={e => updateClientStats('surname', e.target.value)} />
                 </div>
                 <div className="grid grid-cols-2 gap-3">
                   <div>
                      <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Age</label>
                      <input type="number" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-blue-400" defaultValue={activeClient.age || ""} onBlur={e => updateClientStats('age', e.target.value)} />
                   </div>
                   <div>
                      <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Gender</label>
                      <select className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner text-slate-700 focus:ring-2 focus:ring-blue-400" defaultValue={activeClient.gender || ""} onBlur={e => updateClientStats('gender', e.target.value)}>
                         <option value="">Select...</option>
                         <option value="Female">Female</option>
                         <option value="Male">Male</option>
                         <option value="Other">Other</option>
                      </select>
                   </div>
                 </div>

{/* --- MEDICATIONS SECTION --- */}
                 <div className="pt-6 border-t border-slate-100 mt-6">
                    <h4 className="text-[9px] font-black uppercase tracking-widest text-blue-600 mb-3 flex items-center gap-2"><Activity size={14}/> Medical & Prescriptions</h4>
                    <div>
                       <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Current Medications (Name, Reason, Dosage, Timing)</label>
                       <textarea 
                         className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-blue-400 min-h-[100px]" 
                         defaultValue={activeClient.medications || ""} 
                         onBlur={e => updateClientStats('medications', e.target.value)} 
                         placeholder="e.g., Asthma Pump - 2 Puffs - Before Training (Asthma)&#10;No current medications." 
                       />
                    </div>
                 </div>

                 <div className="pt-6 border-t border-slate-100 mt-6">
                    <h4 className="text-[9px] font-black uppercase tracking-widest text-blue-600 mb-3 flex items-center gap-2"><Shield size={14}/> Emergency Contact</h4>
                    <div className="space-y-3">
                       <div>
                          <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Contact Name</label>
                          <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-blue-400" defaultValue={activeClient.emergencyContactName || ""} onBlur={e => updateClientStats('emergencyContactName', e.target.value)} placeholder="e.g., John Doe" />
                       </div>
                       <div>
                          <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Contact Number</label>
                          <input type="tel" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-blue-400" defaultValue={activeClient.emergencyContactPhone || ""} onBlur={e => updateClientStats('emergencyContactPhone', e.target.value)} placeholder="e.g., 082 123 4567" />
                       </div>
                    </div>
                 </div>
              </div>
            </div>
            <div className="text-center">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-center gap-1"><CheckCircle2 size={10} className="text-teal-500"/> Profile syncs automatically</p>
            </div>
          </div>
        )}

        {menuLevel === 'account' && (
          <div className="space-y-6">
             <div className="glass-card p-6 rounded-[2.5rem] space-y-4">
               <h3 className="text-[10px] font-black uppercase text-slate-400 border-b pb-2">Client Credentials</h3>
               <div>
                 <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Update Password</label>
                 <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner" defaultValue={activeClient.password || ""} onBlur={e => updateClientStats('password', e.target.value)} placeholder="Type new password..." />
               </div>
               <p className="text-[9px] text-slate-400 px-1 mt-2 leading-relaxed">If you change this password, the client will immediately need to use the new password on their next login.</p>
             </div>
          </div>
        )}
        
        {menuLevel === 'videos' && (
          <div className="space-y-6">
             <div className="glass-card p-6 rounded-[2rem] space-y-4">
               <h3 className="text-[10px] font-black uppercase text-slate-400 border-b pb-2">Assign Videos</h3>
               {globalVideos.map(vid => {
                 const isAssigned = (Array.isArray(activeClient.assignedVideos) ? activeClient.assignedVideos : []).some(v => v.id === vid.id);
                 return (
                   <div key={vid.id} onClick={() => toggleClientVideo(vid)} className={`p-4 rounded-xl flex justify-between items-center cursor-pointer transition-all border ${isAssigned ? 'bg-orange-50 border-orange-200' : 'bg-white border-slate-100'}`}>
                     <div className="flex items-center gap-3"><PlayCircle size={18} className={isAssigned ? "text-orange-600" : "text-slate-300"}/><span className="text-xs font-bold uppercase text-slate-700">{vid.title}</span></div>
                     {isAssigned && <CheckCircle2 size={18} className="text-orange-500" />}
                   </div>
                 );
               })}
             </div>
          </div>
        )}

        {menuLevel === 'podcasts' && (
          <div className="space-y-6">
             <div className="glass-card p-6 rounded-[2rem] space-y-4">
               <h3 className="text-[10px] font-black uppercase text-slate-400 border-b pb-2">Assign Podcasts</h3>
               {globalPodcasts.map(pod => {
                 const isAssigned = (Array.isArray(activeClient.assignedPodcasts) ? activeClient.assignedPodcasts : []).some(p => p.id === pod.id);
                 return (
                   <div key={pod.id} onClick={() => toggleClientPodcast(pod)} className={`p-4 rounded-xl flex justify-between items-center cursor-pointer transition-all border ${isAssigned ? 'bg-teal-50 border-teal-200' : 'bg-white border-slate-100'}`}>
                     <div className="flex items-center gap-3"><Headphones size={18} className={isAssigned ? "text-teal-600" : "text-slate-300"}/><span className="text-xs font-bold uppercase text-slate-700">{pod.title}</span></div>
                     {isAssigned && <CheckCircle2 size={18} className="text-teal-500" />}
                   </div>
                 );
               })}
             </div>
          </div>
        )}

        {menuLevel === 'training' && (
          <div className="space-y-6">
            <div className="flex gap-2 mb-4">
              <button onClick={() => setSubTab('active')} className={`flex-1 py-3 rounded-xl text-[9px] font-black uppercase border-2 ${subTab === 'active' ? 'border-teal-500 text-teal-700 bg-teal-50' : 'border-transparent text-slate-300'}`}>Active</button>
              <button onClick={() => setSubTab('completed')} className={`flex-1 py-3 rounded-xl text-[9px] font-black uppercase border-2 ${subTab === 'completed' ? 'border-teal-500 text-teal-700 bg-teal-50' : 'border-transparent text-slate-300'}`}>History</button>
            </div>
            {subTab === 'active' ? (
              <div className="glass-card rounded-[2.5rem] p-8 space-y-6 shadow-sm border-white">
                {activeClient.activeProgram ? (
                  <div className="space-y-4 text-center">
                    <div className="p-5 bg-teal-50 rounded-2xl border border-teal-100 space-y-2">
                       <span className="font-bold text-xs uppercase text-teal-800 block">{activeClient.activeProgram.name}</span>
                       <p className="text-[11px] text-slate-600 whitespace-pre-wrap pt-2 leading-relaxed">{activeClient.activeProgram.details}</p>
                       {activeClient.activeProgram.pdfUrl && (
                         <a href={activeClient.activeProgram.pdfUrl} target="_blank" rel="noreferrer" className="w-full py-3 mt-4 bg-teal-600 text-white rounded-xl text-[10px] font-black uppercase flex items-center justify-center gap-2"><FileText size={16} /> Open PDF</a>
                       )}
                    </div>
                    <button onClick={completePlan} className="w-full py-4 bg-teal-600 text-white rounded-xl text-[10px] font-black uppercase">Mark as Completed</button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <input placeholder="Plan Title" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={progName} onChange={e=>setProgName(e.target.value)} />
                    <div className="grid grid-cols-2 gap-3">
                       <input type="date" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={progStart} onChange={e=>setProgStart(e.target.value)} />
                       <input type="date" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={progEnd} onChange={e=>setProgEnd(e.target.value)} />
                    </div>
                    <input placeholder="Days Per Week" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={progDays} onChange={e=>setProgDays(e.target.value)} />
                    <textarea placeholder="List Exercises..." className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none h-32" value={progDetails} onChange={e=>setProgDetails(e.target.value)} />
                    <input placeholder="PDF URL" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none" value={progPdfUrl} onChange={e=>setProgPdfUrl(e.target.value)} />
                    <button onClick={assignProgram} className="w-full py-4 bg-teal-600 text-white rounded-xl text-[9px] font-black uppercase">Assign Program</button>
                  </div>
                )}
                
                {/* --- COACH TRAINING LOGS VIEW --- */}
                <div className="mt-8 border-t border-slate-100 pt-6">
                   <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-800 text-center mb-2">Training Logs</h3>
                   <TrainingCalendar client={activeClient} updateClientStats={updateClientStats} isCoach={true} />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {(Array.isArray(activeClient.pastPrograms) ? activeClient.pastPrograms : []).map(p => (
                  <div key={p.id} className="glass-card p-6 rounded-3xl flex flex-col gap-3">
                    <div className="flex justify-between items-start w-full">
                       <div><p className="font-black text-xs uppercase text-slate-800">{p.name}</p><p className="text-[8px] font-bold text-slate-400">Closed: {p.dateCompleted}</p></div>
                       <CheckCircle2 size={18} className="text-teal-500"/>
                    </div>
                    <p className="text-[10px] text-slate-500 whitespace-pre-wrap leading-relaxed">{p.details}</p>
                    {p.pdfUrl && <a href={p.pdfUrl} target="_blank" rel="noreferrer" className="w-full py-3 bg-slate-50 text-teal-700 rounded-xl text-[9px] font-black uppercase flex items-center justify-center gap-2 mt-2"><Download size={14}/> View PDF Archive</a>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {menuLevel === 'tests' && (
          <div className="space-y-6">
            {!selectedSnapshot && (
              <div className="flex gap-2 mb-4">
                <button onClick={() => setSubTab('clinical')} className={`flex-1 py-3 rounded-xl text-[9px] font-black uppercase border-2 ${subTab === 'clinical' ? 'border-purple-500 text-purple-700 bg-purple-50' : 'border-transparent text-slate-300'}`}>Clinical</button>
                <button onClick={() => setSubTab('history')} className={`flex-1 py-3 rounded-xl text-[9px] font-black uppercase border-2 ${subTab === 'history' ? 'border-purple-500 text-purple-700 bg-purple-50' : 'border-transparent text-slate-300'}`}>History</button>
              </div>
            )}
            
            {subTab === 'clinical' && !selectedSnapshot ? (
              <div className="space-y-6 pb-12">
                <div className="glass-card p-6 rounded-[2.5rem] shadow-sm flex justify-between items-center mb-6">
                     <span className="text-[10px] font-black uppercase text-slate-800">Date</span>
                     <input type="date" value={testDate} onChange={e => setTestDate(e.target.value)} className="text-[10px] bg-transparent font-bold text-purple-600 outline-none text-right"/>
                </div>

                {Object.entries(CLINICAL_SCHEMAS_GRID).map(([key, schema]) => (
                  <ClinicalGrid key={key} schemaKey={key} schemaObj={schema} isEdit={true} dataSource={activeClient} onChange={updateClientStats} />
                ))}

                {Object.entries(CLINICAL_SCHEMAS_TEXT).map(([key, schema]) => (
                  <ClinicalTextAreas key={key} schemaKey={key} schemaObj={schema} isEdit={true} dataSource={activeClient} onChange={updateClientStats} />
                ))}

                <div className="glass-card p-6 rounded-[2.5rem] shadow-sm border-white mb-6">
                  <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-800 border-b pb-3 mb-4">Postural Analysis</h3>
                  <BodyMap markers={activeClient.assessments?.posture} isEditing={true} onMapClick={handleMarkerClick} />
                  <div className="space-y-4 mt-6">
                    <div>
                      <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Outcome & Flagged Areas</label>
                      <textarea className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 min-h-[100px]" placeholder="Observations..." defaultValue={activeClient.posturalNotes || ""} onBlur={e => updateClientStats('posturalNotes', e.target.value)} />
                    </div>
                    <div>
                      <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Postural Assessment PDF Link</label>
                      <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1" placeholder="URL to attached PDF..." defaultValue={activeClient.posturalPdfUrl || ""} onBlur={e => updateClientStats('posturalPdfUrl', e.target.value)} />
                    </div>
                  </div>
                </div>

                <button onClick={saveClinicalSnapshot} className="w-full py-5 bg-purple-600 text-white rounded-[1.5rem] text-[10px] font-black uppercase shadow-lg">Save Full Assessment</button>
              </div>
            ) : subTab === 'history' && !selectedSnapshot ? (
              <div className="space-y-4">
                 {(Array.isArray(activeClient.testResultsHistory) ? activeClient.testResultsHistory : []).map(snap => (
                   <div key={snap.id} onClick={() => setSelectedSnapshot(snap)} className="glass-card p-6 rounded-[2rem] border-white space-y-3 cursor-pointer hover:bg-slate-50 transition-colors btn-action">
                      <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black uppercase text-purple-700">{snap.date}</span>
                        <span className="text-[9px] font-bold text-slate-400 flex items-center gap-1">View Report <ChevronRight size={12}/></span>
                      </div>
                      <div className="text-[9px] text-slate-500 italic truncate">{snap.posturalNotes || "Assessment Log"}</div>
                   </div>
                 ))}
                 {(!activeClient.testResultsHistory || activeClient.testResultsHistory.length === 0) && <p className="text-center text-[10px] uppercase text-slate-300 py-10">No History Available</p>}
              </div>
            ) : null}

            {selectedSnapshot && (
              <AssessmentReport snapshot={selectedSnapshot} client={activeClient} onBack={() => setSelectedSnapshot(null)} />
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 max-w-lg mx-auto pt-10 animate-in">
      <div className="flex justify-between items-end mb-10 px-2">
        <h1 className="text-2xl font-black uppercase tracking-tighter text-slate-900 leading-none">Coach Portal</h1>
        <div className="flex gap-2">
          {/* TEMPORARY MIGRATION BUTTON */}
          <button onClick={migrateOldSessions} className="p-3 bg-red-50 rounded-xl shadow-sm text-red-600 font-black text-[9px] uppercase btn-action">Migrate Old Data</button>
          
          <button onClick={() => setView('schedule')} className="p-3 bg-white rounded-xl shadow-sm text-indigo-600 btn-action"><Calendar size={18}/></button>
          <button onClick={() => setView('podcasts')} className="p-3 bg-white rounded-xl shadow-sm text-teal-600 btn-action"><Headphones size={18}/></button>
          <button onClick={() => setView('library')} className="p-3 bg-white rounded-xl shadow-sm text-orange-500 btn-action"><Film size={18}/></button>
          <button onClick={() => setAppMode('login')} className="p-3 bg-white rounded-xl shadow-sm text-slate-400 btn-action"><LogOut size={16}/></button>
        </div>
      </div>
      <div className="glass-card rounded-[2rem] p-6 mb-8 shadow-sm">
        <h3 className="text-[9px] font-black uppercase tracking-[0.2em] mb-4 flex items-center gap-2">New Client</h3>
        <input placeholder="Client Name" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none mb-2 shadow-inner border-none" value={newName} onChange={e=>setNewName(e.target.value)} />
        <input placeholder="Email Address" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none mb-2 shadow-inner border-none" value={newEmail} onChange={e=>setNewEmail(e.target.value)} />
        <input placeholder="Assign a Password" type="text" className="w-full p-4 bg-white/50 rounded-xl text-xs font-bold outline-none mb-4 shadow-inner border-none" value={newPassword} onChange={e=>setNewPassword(e.target.value)} />
        <button onClick={createClient} className="w-full py-4 bg-slate-900 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg">Establish Profile</button>
      </div>
      <div className="space-y-3 pb-12">
        <h4 className="text-[9px] font-black uppercase text-slate-400 px-2 mb-2 tracking-widest">Client Roster</h4>
        {clients.map(c => (
          <div key={c.id} onClick={() => { setActiveClient(c); setView('edit'); }} className="glass-card rounded-2xl p-4 flex items-center gap-4 cursor-pointer group border-white">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center shrink-0 border border-white text-teal-700 font-black text-[10px] uppercase shadow-sm">{c.name?.substring(0,2).toUpperCase()}</div>
            <div className="flex-1 min-w-0 font-bold text-xs uppercase truncate tracking-widest">{c.name} {c.surname || ""}</div>
            <ChevronRight size={14} className="text-slate-200 group-hover:text-teal-500" />
          </div>
        ))}
      </div>
    </div>
  );
}

function ClientView({ client, setAppMode }) {
  const [tab, setTab] = useState('home');
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  
  const [showTutorial, setShowTutorial] = useState(client.hasSeenTutorial !== true);

  const closeTutorial = async () => {
    setShowTutorial(false);
    if (client.hasSeenTutorial !== true) {
      await updateDoc(doc(db, 'clients', client.id), { hasSeenTutorial: true });
    }
  };

  const updateProfile = async (field, value) => {
    try {
      await updateDoc(doc(db, 'clients', client.id), { [field]: value });
    } catch (e) {
      console.error("Error updating profile:", e);
    }
  };

  const updateClientStats = async (path, val) => {
    try {
      await updateDoc(doc(db, 'clients', client.id), { [path]: val });
    } catch (e) {
      console.error("Error updating stats:", e);
    }
  };

  if (!client) return <div className="min-h-screen flex items-center justify-center bg-wellness"><Loader className="animate-spin text-teal-600" size={20} /></div>;

  return (
    <div className="min-h-screen pb-24 max-w-md mx-auto">
      
      {/* WELCOME TUTORIAL MODAL */}
      {showTutorial && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/40 backdrop-blur-md animate-in">
          <div className="bg-white w-full max-w-sm rounded-[3rem] p-8 shadow-2xl relative overflow-hidden flex flex-col max-h-[90vh]">
            <button onClick={closeTutorial} className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 z-10 btn-action"><X size={20}/></button>
            
            <div className="overflow-y-auto scrollbar-hide -mx-2 px-2 pb-4">
                <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-2xl flex items-center justify-center mb-6 mt-2"><Activity size={24}/></div>
                <h2 className="text-2xl font-black uppercase tracking-tighter text-slate-900 mb-2">Welcome to your Portal</h2>
                <p className="text-xs font-medium text-slate-500 mb-6 leading-relaxed">Here is a quick guide on how to navigate your new wellness dashboard.</p>

                {/* WELCOME IMAGE */}
                <div className="w-full h-48 bg-slate-100 rounded-2xl mb-8 overflow-hidden shadow-inner relative">
                   <img 
                      src="mac pic.jpg" 
                      alt="Coach Macaela" 
                      className="w-full h-full object-cover object-top"
                      onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=2070&auto=format&fit=crop"; }}
                   />
                </div>

                <div className="space-y-5 mb-8">
                  <div className="flex gap-4 items-center"><div className="w-10 h-10 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center shrink-0"><Dumbbell size={18}/></div><div><p className="text-[10px] font-black uppercase tracking-widest text-slate-800">1. Your Program</p><p className="text-[9px] text-slate-500 mt-1">View your current active training protocol.</p></div></div>
                  <div className="flex gap-4 items-center"><div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center shrink-0"><ClipboardList size={18}/></div><div><p className="text-[10px] font-black uppercase tracking-widest text-slate-800">2. Test Results</p><p className="text-[9px] text-slate-500 mt-1">Track your clinical data and postural maps.</p></div></div>
                  <div className="flex gap-4 items-center"><div className="w-10 h-10 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center shrink-0"><Film size={18}/></div><div><p className="text-[10px] font-black uppercase tracking-widest text-slate-800">3. Media Library</p><p className="text-[9px] text-slate-500 mt-1">Watch videos & listen to assigned podcasts.</p></div></div>
                </div>
            </div>

            <button onClick={closeTutorial} className="w-full py-4 mt-auto bg-teal-600 text-white rounded-[1.5rem] text-[10px] font-black uppercase tracking-widest shadow-lg btn-action shrink-0">Let's Get Started</button>
          </div>
        </div>
      )}

      <div className="p-6 space-y-6 animate-in">
        <div className="flex justify-between items-center pt-2 no-print">
           <div className="text-[9px] font-black tracking-[0.3em] text-teal-600 uppercase">BTM Portal</div>
           <div className="flex gap-2">
             <button onClick={() => setTab('faq')} className="p-2 bg-white rounded-lg shadow-sm text-teal-600 border border-slate-50 btn-action"><HelpCircle size={14}/></button>
             <button onClick={() => setAppMode('login')} className="p-2 bg-white rounded-lg shadow-sm text-slate-300 border border-slate-50 btn-action"><LogOut size={14}/></button>
           </div>
        </div>

        {/* --- PROFILE TAB --- */}
        {tab === 'profile' && (
          <div className="space-y-6 animate-in pb-20">
            <button onClick={() => setTab('home')} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-300 btn-action hover:text-slate-600"><ArrowLeft size={14}/> Dashboard</button>
            <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 leading-none text-center">My Profile</h2>
            
            <div className="glass-card p-8 rounded-[3rem] shadow-xl border-white space-y-4 relative">
              <div className="flex justify-center mb-6">
                 <div className="w-20 h-20 bg-teal-50 text-teal-600 rounded-[2rem] flex items-center justify-center shadow-inner border border-teal-100"><User size={36}/></div>
              </div>
              
              <div className="space-y-3">
                 <div>
                    <label className="text-[7px] font-black uppercase text-slate-400 ml-1">First Name</label>
                    <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-teal-400" defaultValue={client.name || ""} onBlur={e => updateProfile('name', e.target.value)} />
                 </div>
                 <div>
                    <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Surname</label>
                    <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-teal-400" defaultValue={client.surname || ""} onBlur={e => updateProfile('surname', e.target.value)} />
                 </div>
                 <div className="grid grid-cols-2 gap-3">
                   <div>
                      <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Age</label>
                      <input type="number" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-teal-400" defaultValue={client.age || ""} onBlur={e => updateProfile('age', e.target.value)} />
                   </div>
                   <div>
                      <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Gender</label>
                      <select className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner text-slate-700 focus:ring-2 focus:ring-teal-400" defaultValue={client.gender || ""} onBlur={e => updateProfile('gender', e.target.value)}>
                         <option value="">Select...</option>
                         <option value="Female">Female</option>
                         <option value="Male">Male</option>
                         <option value="Other">Other</option>
                      </select>
                   </div>
                 </div>

{/* --- MEDICATIONS SECTION --- */}
                 <div className="pt-6 border-t border-slate-100 mt-6">
                    <h4 className="text-[9px] font-black uppercase tracking-widest text-teal-600 mb-3 flex items-center gap-2"><Activity size={14}/> Medical & Prescriptions</h4>
                    <div>
                       <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Current Medications (Name, Reason, Dosage, Timing)</label>
                       <textarea 
                         className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-teal-400 min-h-[100px]" 
                         defaultValue={client.medications || ""} 
                         onBlur={e => updateProfile('medications', e.target.value)} 
                         placeholder="e.g., Asthma Pump - 2 Puffs - Before Training (Asthma)&#10;No current medications." 
                       />
                    </div>
                 </div>

                 <div className="pt-6 border-t border-slate-100 mt-6">
                    <h4 className="text-[9px] font-black uppercase tracking-widest text-teal-600 mb-3 flex items-center gap-2"><Shield size={14}/> Emergency Contact</h4>
                    <div className="space-y-3">
                       <div>
                          <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Contact Name</label>
                          <input className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-teal-400" defaultValue={client.emergencyContactName || ""} onBlur={e => updateProfile('emergencyContactName', e.target.value)} placeholder="e.g., John Doe" />
                       </div>
                       <div>
                          <label className="text-[7px] font-black uppercase text-slate-400 ml-1">Contact Number</label>
                          <input type="tel" className="w-full p-4 bg-slate-50 rounded-xl text-xs font-bold outline-none mt-1 shadow-inner focus:ring-2 focus:ring-teal-400" defaultValue={client.emergencyContactPhone || ""} onBlur={e => updateProfile('emergencyContactPhone', e.target.value)} placeholder="e.g., 082 123 4567" />
                       </div>
                    </div>
                 </div>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center justify-center gap-1"><CheckCircle2 size={10} className="text-teal-500"/> Changes save automatically</p>
            </div>
          </div>
        )}

        {/* --- FAQ TAB --- */}
        {tab === 'faq' && (
          <div className="space-y-6 animate-in pb-20">
            <button onClick={() => setTab('home')} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-300 hover:text-slate-600 transition-colors btn-action"><ArrowLeft size={14}/> Dashboard</button>
            <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 leading-none text-center">FAQ & Guide</h2>
            
            <div className="space-y-4">
              <div className="glass-card p-6 rounded-[2.5rem] border-white shadow-sm space-y-4">
                 <h3 className="text-[10px] font-black uppercase tracking-widest text-teal-600 border-b border-slate-100 pb-2 flex items-center gap-2"><Activity size={14}/> Using the App</h3>
                 <div className="space-y-4">
                    <div><p className="text-xs font-bold text-slate-800">How do I change my password?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">For the security of your data, passwords are currently managed on the coaching side. If you have forgotten your password or wish to change it, please drop Coach Macaela a quick message to securely update it.</p></div>
                    <div><p className="text-xs font-bold text-slate-800">How do I update my personal details or emergency contact?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">On your main dashboard, simply tap your name (next to the little user icon) at the very top of the screen. This will open your Profile page where you can update your age, gender, and emergency contact details. Changes save automatically!</p></div>
                    <div><p className="text-xs font-bold text-slate-800">A video or podcast isn't playing properly. What should I do?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">First, ensure you have a stable internet connection. If the media player inside the app isn't loading, you can usually click the title of the video/podcast to open it directly in your native YouTube or Spotify app.</p></div>
                 </div>
              </div>

              <div className="glass-card p-6 rounded-[2.5rem] border-white shadow-sm space-y-4">
                 <h3 className="text-[10px] font-black uppercase tracking-widest text-purple-600 border-b border-slate-100 pb-2 flex items-center gap-2"><Dumbbell size={14}/> Training & Logging</h3>
                 <div className="space-y-4">
                    <div><p className="text-xs font-bold text-slate-800">Where do I find my daily exercises?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">Tap the Active Program (Dumbbell icon) on your home screen. Here you will see the details of your current training block, how many days a week you should be active, and a button to download your full PDF program.</p></div>
                    <div><p className="text-xs font-bold text-slate-800">How do I log my workouts?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">Right below your Active Program, you will see your Training Logs calendar! Tap on today's date to select the type of training you completed and use the slider to log your duration in minutes.</p></div>
                    <div><p className="text-xs font-bold text-slate-800">What if an exercise in my program causes pain?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">Stop immediately. Because our programming focuses heavily on precise prehabilitation and biomechanics, it is crucial that you do not push through sharp or sudden pain. Please refer back to the coaching cues in your assigned Video Library, or reach out directly so we can regress or modify the movement.</p></div>
                 </div>
              </div>

              <div className="glass-card p-6 rounded-[2.5rem] border-white shadow-sm space-y-4">
                 <h3 className="text-[10px] font-black uppercase tracking-widest text-orange-600 border-b border-slate-100 pb-2 flex items-center gap-2"><ClipboardList size={14}/> Assessments & Data</h3>
                 <div className="space-y-4">
                    <div><p className="text-xs font-bold text-slate-800">What do the red markers on my Postural Analysis map mean?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">The markers on your body map indicate specific areas of muscular imbalance, postural deviations, or past injury sites that were identified during your movement assessment. Check the "Outcome & Flagged Areas" notes right below the map to read exactly how your programming targets these areas.</p></div>
                    <div><p className="text-xs font-bold text-slate-800">Where is my old data?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">Under the Test Results tab, scroll to the very bottom to find your Assessment History. You can tap on any past date to pull up a full report of your body composition, flexibility, and functional test scores from that specific day to compare with your current stats.</p></div>
                    <div><p className="text-xs font-bold text-slate-800">Can I print my test results?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">Yes! Go to your Test Results, tap on the assessment you want to keep, and click the "Save PDF" button at the top. This will generate a clean, professional report for your personal records or to share with other wellness professionals.</p></div>
                 </div>
              </div>

              <div className="glass-card p-6 rounded-[2.5rem] border-white shadow-sm space-y-4">
                 <h3 className="text-[10px] font-black uppercase tracking-widest text-indigo-600 border-b border-slate-100 pb-2 flex items-center gap-2"><Target size={14}/> Special Programs & Studies</h3>
                 <div className="space-y-4">
                    <div><p className="text-xs font-bold text-slate-800">What is the Case Study tab?</p><p className="text-[10px] text-slate-500 mt-1 leading-relaxed">If you have been selected to participate in a specific coaching study (like the UCS protocol), a special Case Study button will appear on your dashboard. You will use this section to view your targeted protocol and check off your compliance every second day over the 28-day period.</p></div>
                 </div>
              </div>

            </div>
          </div>
        )}

        {/* --- CLIENT CASE STUDY TAB --- */}
        {tab === 'casestudy' && client.isCaseStudyParticipant && (
          <div className="space-y-6 animate-in pb-20">
            <button onClick={() => setTab('home')} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-300 hover:text-slate-900 transition-colors"><ArrowLeft size={14}/> Dashboard</button>
            <h2 className="text-3xl font-black uppercase tracking-tighter text-indigo-900 leading-none text-center">Case Study</h2>
            
            <UCSCaseStudyProtocol />
            <CaseStudyGrid client={client} updateClientStats={updateClientStats} isCoach={false} />
          </div>
        )}

        {tab === 'home' && (
          <div className="space-y-6">
            <header className="py-2 text-center flex flex-col items-center">
              <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase mb-1 leading-none">My Wellness</h1>
              <button 
                onClick={() => setTab('profile')} 
                className="text-[10px] font-black text-teal-600 uppercase tracking-[0.4em] opacity-70 hover:opacity-100 flex items-center justify-center gap-2 transition-all btn-action mt-2 bg-teal-50 px-4 py-2 rounded-full border border-teal-100 shadow-sm"
              >
                <User size={12} /> {client.name} {client.surname || ""}
              </button>
            </header>
            <div className="grid grid-cols-1 gap-3">
              {client.isCaseStudyParticipant && (
                <button onClick={() => setTab('casestudy')} className="glass-card p-8 rounded-[2rem] flex items-center justify-between group shadow-md btn-action border border-indigo-200 bg-indigo-50/50">
                  <div className="flex items-center gap-4">
                     <div className="p-4 bg-indigo-100 rounded-2xl text-indigo-700"><BookOpen size={24}/></div>
                     <div className="text-left"><span className="font-black uppercase text-sm tracking-widest text-indigo-900 block">UCS Case Study</span></div>
                  </div>
                  <ChevronRight size={20} className="text-indigo-300"/>
                </button>
              )}
              
              <button onClick={() => setTab('program')} className="glass-card p-8 rounded-[2rem] flex items-center justify-between group border-white shadow-sm btn-action">
                <div className="flex items-center gap-4">
                   <div className="p-4 bg-teal-50 rounded-2xl text-teal-600"><Dumbbell size={24}/></div>
                   <div className="text-left"><span className="font-black uppercase text-sm tracking-widest text-slate-800 block">Active Program</span></div>
                </div>
                <ChevronRight size={20} className="text-slate-200"/>
              </button>

              <button onClick={() => window.open('https://www.myfitnesspal.com', '_blank', 'noopener,noreferrer')} className="glass-card p-8 rounded-[2rem] flex items-center justify-between group border-white shadow-sm btn-action">
                <div className="flex items-center gap-4">
                   <div className="p-4 bg-emerald-50 rounded-2xl text-emerald-600"><Apple size={24}/></div>
                   <div className="text-left"><span className="font-black uppercase text-sm tracking-widest text-slate-800 block">Nutrition</span></div>
                </div>
                <ChevronRight size={20} className="text-slate-200"/>
              </button>

              <button onClick={() => setTab('testing')} className="glass-card p-8 rounded-[2rem] flex items-center justify-between group border-white shadow-sm btn-action">
                <div className="flex items-center gap-4">
                   <div className="p-4 bg-purple-50 rounded-2xl text-purple-600"><ClipboardList size={24}/></div>
                   <div className="text-left"><span className="font-black uppercase text-sm tracking-widest text-slate-800 block">Test Results</span></div>
                </div>
                <ChevronRight size={20} className="text-slate-200"/>
              </button>
              <button onClick={() => setTab('videos')} className="glass-card p-8 rounded-[2rem] flex items-center justify-between group border-white shadow-sm btn-action">
                <div className="flex items-center gap-4">
                   <div className="p-4 bg-orange-50 rounded-2xl text-orange-600"><Film size={24}/></div>
                   <div className="text-left"><span className="font-black uppercase text-sm tracking-widest text-slate-800 block">Video Library</span></div>
                </div>
                <ChevronRight size={20} className="text-slate-200"/>
              </button>
              <button onClick={() => setTab('podcasts')} className="glass-card p-8 rounded-[2rem] flex items-center justify-between group border-white shadow-sm btn-action">
                <div className="flex items-center gap-4">
                   <div className="p-4 bg-blue-50 rounded-2xl text-blue-600"><Headphones size={24}/></div>
                   <div className="text-left"><span className="font-black uppercase text-sm tracking-widest text-slate-800 block">Podcast Library</span></div>
                </div>
                <ChevronRight size={20} className="text-slate-200"/>
              </button>
              <button onClick={() => setTab('history')} className="glass-card p-8 rounded-[2rem] flex items-center justify-between group border-white shadow-sm btn-action">
                <div className="flex items-center gap-4">
                   <div className="p-4 bg-slate-50 rounded-2xl text-slate-400"><History size={24}/></div>
                   <div className="text-left"><span className="font-black uppercase text-sm tracking-widest text-slate-800 block">Archives</span></div>
                </div>
                <ChevronRight size={20} className="text-slate-200"/>
              </button>
              <button onClick={() => setTab('faq')} className="glass-card p-8 rounded-[2rem] flex items-center justify-between group border-white shadow-sm btn-action">
                <div className="flex items-center gap-4">
                   <div className="p-4 bg-indigo-50 rounded-2xl text-indigo-600"><HelpCircle size={24}/></div>
                   <div className="text-left"><span className="font-black uppercase text-sm tracking-widest text-slate-800 block">FAQ & Guide</span></div>
                </div>
                <ChevronRight size={20} className="text-slate-200"/>
              </button>
            </div>
          </div>
        )}

        {tab === 'testing' && !selectedSnapshot && (
          <div className="space-y-6 animate-in pb-20">
            <button onClick={() => setTab('home')} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-300"><ArrowLeft size={14}/> Dashboard</button>
            <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 leading-none">Testing</h2>
            
            <div className="space-y-6">
              <div className="glass-card p-5 rounded-[2rem] shadow-sm mb-6 text-center">
                 <p className="text-[10px] font-black text-purple-600 uppercase tracking-widest">Showing Latest Data</p>
              </div>

              {/* Map over the schemas to render data block for clients */}
              {Object.entries(CLINICAL_SCHEMAS_GRID).map(([key, schema]) => (
                <ClinicalGrid key={key} schemaKey={key} schemaObj={schema} isEdit={false} dataSource={client} />
              ))}

              {Object.entries(CLINICAL_SCHEMAS_TEXT).map(([key, schema]) => (
                <ClinicalTextAreas key={key} schemaKey={key} schemaObj={schema} isEdit={false} dataSource={client} />
              ))}

              <div className="glass-card p-8 rounded-[3rem] shadow-xl border-white space-y-6">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 border-b pb-3 text-center">Postural Analysis</h3>
                <BodyMap markers={client.assessments?.posture || client.posturalMap} isEditing={false} />
                <p className="text-xs font-bold leading-relaxed text-slate-700 whitespace-pre-wrap bg-slate-50 p-4 rounded-xl shadow-inner">{client.posturalNotes || "No notes logged."}</p>
                {client.posturalPdfUrl && (
                  <a href={client.posturalPdfUrl} target="_blank" rel="noreferrer" className="w-full py-4 mt-2 bg-purple-600 text-white rounded-xl text-[10px] font-black uppercase flex items-center justify-center gap-2 shadow-lg"><FileText size={16}/> Open Postural PDF</a>
                )}
              </div>

              <div className="space-y-4 pt-10">
                 <h4 className="text-[10px] font-black uppercase text-slate-400 px-2 tracking-widest text-center">Assessment History</h4>
                 {(Array.isArray(client.testResultsHistory) ? client.testResultsHistory : []).map(snap => (
                   <div key={snap.id} onClick={() => setSelectedSnapshot(snap)} className="glass-card p-5 rounded-2xl flex justify-between items-center border-white cursor-pointer hover:bg-slate-50 transition-all btn-action">
                     <div><p className="font-bold text-xs uppercase text-slate-700">Assessment</p><p className="text-[8px] font-black text-slate-300 uppercase">{snap.date}</p></div>
                     <div className="text-purple-500 font-bold text-xs flex items-center gap-1">View Report <ChevronRight size={14}/></div>
                   </div>
                 ))}
                 {(!client.testResultsHistory || client.testResultsHistory.length === 0) && <p className="text-center text-[10px] uppercase text-slate-300">No previous records.</p>}
              </div>
            </div>
          </div>
        )}

        {tab === 'testing' && selectedSnapshot && (
           <AssessmentReport snapshot={selectedSnapshot} client={client} onBack={() => setSelectedSnapshot(null)} />
        )}

        {tab === 'program' && (
          <div className="space-y-6 animate-in pb-20">
            <button onClick={() => setTab('home')} className="flex items-center gap-2 text-[9px] font-black uppercase text-slate-300"><ArrowLeft size={14}/> Dashboard</button>
            {client.activeProgram ? (
              <div className="glass-card p-10 rounded-[4rem] shadow-2xl space-y-8 border-white text-center border-2">
                <div className="p-6 bg-teal-50 rounded-full w-24 h-24 mx-auto flex items-center justify-center text-teal-600 shadow-inner"><Dumbbell size={40}/></div>
                <div><h3 className="text-2xl font-black text-teal-800 uppercase tracking-tight mb-2 leading-tight">{client.activeProgram.name}</h3><p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Date: {client.activeProgram.dateAssigned}</p></div>
                <p className="text-xs font-bold text-slate-700 whitespace-pre-wrap bg-slate-50 p-6 rounded-2xl shadow-inner">{client.activeProgram.details}</p>
                {client.activeProgram.pdfUrl && <a href={client.activeProgram.pdfUrl} target="_blank" rel="noreferrer" className="w-full py-4 mt-4 bg-teal-600 text-white rounded-[1.5rem] text-[10px] font-black uppercase flex items-center justify-center gap-2 shadow-lg hover:bg-teal-700"><Download size={18}/> Open PDF Program</a>}
              </div>
            ) : (
              <div className="text-center py-40 glass-card rounded-[3.5rem] text-slate-300 uppercase text-[11px] font-black tracking-[0.4em] border-white shadow-sm">No Active Protocol</div>
            )}
            
            {/* --- CLIENT TRAINING LOGS VIEW --- */}
            <div className="mt-8">
               <h3 className="text-[11px] font-black uppercase tracking-widest text-slate-800 text-center mb-2">My Training Logs</h3>
               <TrainingCalendar client={client} updateClientStats={updateClientStats} isCoach={false} />
            </div>
          </div>
        )}

        {tab === 'history' && (
          <div className="space-y-6 animate-in pb-20">
            <button onClick={() => setTab('home')} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-300 hover:text-slate-900 transition-colors"><ArrowLeft size={14}/> Dashboard</button>
            <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 leading-none text-center">Archives</h2>
            <div className="space-y-4">
              {(Array.isArray(client.pastPrograms) ? client.pastPrograms : []).length > 0 ? (Array.isArray(client.pastPrograms) ? client.pastPrograms : []).map(p => (
                <div key={p.id} className="glass-card p-8 rounded-[2.5rem] border-white shadow-sm flex flex-col gap-4">
                   <div className="flex justify-between items-start w-full">
                     <div className="flex gap-4">
                       <div className="p-3 bg-slate-50 rounded-2xl text-slate-400 shrink-0 h-fit"><FileText size={20}/></div>
                       <div>
                         <h3 className="text-[11px] font-black uppercase text-slate-800 tracking-widest">{p.name}</h3>
                         <p className="text-[9px] font-bold text-slate-300 uppercase mb-2">Closed: {p.dateCompleted}</p>
                         <p className="text-[10px] text-slate-500 whitespace-pre-wrap leading-relaxed">{p.details}</p>
                       </div>
                     </div>
                     <CheckCircle2 size={20} className="text-teal-500 shrink-0"/>
                   </div>
                   {p.pdfUrl && <a href={p.pdfUrl} target="_blank" rel="noreferrer" className="w-full py-3 bg-slate-50 text-teal-700 rounded-xl text-[9px] font-black uppercase flex items-center justify-center gap-2 mt-2 hover:bg-slate-100 transition-colors"><Download size={14}/> Download PDF</a>}
                </div>
              )) : <div className="text-center py-24 glass-card rounded-[3.5rem] text-slate-200 font-black uppercase text-[11px] tracking-[0.5em] border-white text-center">Archives Clean</div>}
            </div>
          </div>
        )}

        {tab === 'videos' && (
          <div className="space-y-6 animate-in pb-20">
             <button onClick={() => setTab('home')} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-300"><ArrowLeft size={14}/> Dashboard</button>
             <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 leading-none text-center">Videos</h2>
             <div className="space-y-4">
               {(Array.isArray(client.assignedVideos) ? client.assignedVideos : []).length > 0 ? (Array.isArray(client.assignedVideos) ? client.assignedVideos : []).map(v => (
                 <div key={v.id} className="glass-card p-0 rounded-[2rem] overflow-hidden shadow-md">
                    {getYoutubeId(v.url) ? (
                      <iframe className="w-full aspect-video" src={`https://www.youtube.com/embed/${getYoutubeId(v.url)}`} title={v.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                    ) : (
                      <a href={v.url} target="_blank" rel="noreferrer" className="p-6 flex items-center justify-between">
                         <div className="flex items-center gap-4"><PlayCircle size={24} className="text-orange-500"/><span className="font-bold text-sm uppercase text-slate-800">{v.title}</span></div>
                         <ChevronRight size={16} className="text-slate-300"/>
                      </a>
                    )}
                    {getYoutubeId(v.url) && <div className="p-4 text-center"><p className="font-bold text-xs uppercase text-slate-800">{v.title}</p></div>}
                 </div>
               )) : <p className="text-center text-[10px] font-black uppercase text-slate-300 py-20 tracking-widest">No Content</p>}
             </div>
          </div>
        )}

        {tab === 'podcasts' && (
          <div className="space-y-6 animate-in pb-20">
             <button onClick={() => setTab('home')} className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-300"><ArrowLeft size={14}/> Dashboard</button>
             <h2 className="text-3xl font-black uppercase tracking-tighter text-slate-900 leading-none text-center">Podcasts</h2>
             <div className="space-y-4">
               {(Array.isArray(client.assignedPodcasts) ? client.assignedPodcasts : []).length > 0 ? (Array.isArray(client.assignedPodcasts) ? client.assignedPodcasts : []).map(p => (
                 <div key={p.id} className="glass-card p-0 rounded-[2rem] overflow-hidden shadow-md">
                    {getYoutubeId(p.url) ? (
                      <iframe className="w-full aspect-video" src={`https://www.youtube.com/embed/${getYoutubeId(p.url)}`} title={p.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                    ) : (
                      <a href={p.url} target="_blank" rel="noreferrer" className="p-6 flex items-center justify-between">
                         <div className="flex items-center gap-4"><Headphones size={24} className="text-teal-500"/><span className="font-bold text-sm uppercase text-slate-800">{p.title}</span></div>
                         <ChevronRight size={16} className="text-slate-300"/>
                      </a>
                    )}
                    {getYoutubeId(p.url) && <div className="p-4 text-center"><p className="font-bold text-xs uppercase text-slate-800">{p.title}</p></div>}
                 </div>
               )) : <p className="text-center text-[10px] font-black uppercase text-slate-300 py-20 tracking-widest">No Content</p>}
             </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-10 left-10 right-10 glass-card rounded-full p-3 flex justify-between items-center shadow-[0_30px_60px_rgba(0,0,0,0.12)] border border-white/60 z-50 no-print">
         <button onClick={() => setTab('home')} className={`flex flex-col items-center gap-1 p-3 flex-1 transition-all ${tab==='home'?'text-teal-600 scale-110 font-black':'text-slate-400 opacity-60'}`}><Activity size={24}/><span className="text-[7px] uppercase font-black tracking-widest text-center">Home</span></button>
         <button onClick={() => setTab('program')} className={`flex flex-col items-center gap-1 p-3 flex-1 transition-all ${tab==='program'?'text-teal-600 scale-110 font-black':'text-slate-400 opacity-60'}`}><Calendar size={24}/><span className="text-[7px] uppercase font-black tracking-widest text-center">Plan</span></button>
         <button onClick={() => setTab('testing')} className={`flex flex-col items-center gap-1 p-3 flex-1 transition-all ${tab==='testing'?'text-teal-600 scale-110 font-black':'text-slate-400 opacity-60'}`}><TrendingUp size={24}/><span className="text-[7px] uppercase font-black tracking-widest text-center">Body</span></button>
      </div>
    </div>
  );
}